import ExcelJS from 'exceljs';
import { listingTemplateAPI, listingTemplateFileAPI, imageStorageAPI } from './api';
import { runExportImageDownloadBatch } from './downloadCenter';
import { getFileFromIndexedDB } from './fileStorage';
import { canonicalListingTemplatePlatform } from './listingTemplatePlatformUtils';
import {
  getListingTemplateDataStartRowIndex,
  resolveListingTemplateMainSheetName,
} from './listingTemplateExcelUtils';
import { propagateSheinSharedAttributeValues } from '@/pages/ipe/workbench/utils/listingTemplateSectionLayout';
import {
  extractSkuPairingKey,
  extractSkcPairingKey,
  diagnoseNthDetailImagePairing,
  diagnoseNthDetailImagePairingBySkc,
  matchNthDetailImageBySkuKey,
  matchNthDetailImageBySkcKey,
} from '@/pages/ipe/workbench/utils/skuNumberPairing';
import {
  resolveExportTitleTargetLanguage,
  shouldTranslateExportTitle,
  textLooksLikeLanguage,
} from './exportTitleTranslate';
import {
  clearExportExternalUrlCache,
  isAlreadyPublicExportUrl,
  toExportPublicUrl,
} from './exportPublicImageUrl';
import {
  isTemuProductLevelHeaderName,
  resolveTemuProductLevelByGroupRowIndex,
} from './temuProductLevelExport';
import { resolveExportHeaderType } from './exportHeaderMappingPolicy';

/** TEMU colIndexMap 为 0-based；ExcelJS 列号为 1-based */
function excelColumnFromTemuColIndexMap(colIndexMap, dataColOrdinal) {
  if (!Array.isArray(colIndexMap) || dataColOrdinal < 0) return null;
  const zeroBased = colIndexMap[dataColOrdinal];
  if (zeroBased == null || zeroBased === '') return null;
  const n = Number(zeroBased);
  if (!Number.isFinite(n) || n < 0) return null;
  return n + 1;
}

/** 收集同名表头对应的全部 Excel 列（TEMU 中/英「商品轮播图N」会重复） */
function pushExcelHeaderColumn(columnsByName, headerName, colNumber) {
  const key = String(headerName || '').trim();
  const col = Number(colNumber);
  if (!key || !Number.isFinite(col) || col < 1) return;
  if (!columnsByName[key]) columnsByName[key] = [];
  if (!columnsByName[key].includes(col)) columnsByName[key].push(col);
}

/** 从「商品轮播图3 / Carousel Image 3」等表头解析序号 */
function extractCarouselImageIndex(headerName) {
  const h = String(headerName || '');
  const match = h.match(/商品轮播图\s*(\d+)/i)
    || h.match(/carousel\s*image\s*(\d+)/i)
    || h.match(/(?:^|[^a-z])轮播图\s*(\d+)/i);
  if (!match) return null;
  const n = parseInt(match[1], 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** SKC/编号配对/结果图：合并预览行中的细节图与主图，避免只认 detailImages */
function collectExportDetailImages(data) {
  const list = [];
  const seen = new Set();
  const push = (img) => {
    if (!img || typeof img !== 'object') return;
    const url = String(img.url || img.previewUrl || img.src || img.imageUrl || '').trim();
    if (!url || seen.has(url)) return;
    seen.add(url);
    list.push({
      ...img,
      url,
      previewUrl: String(img.previewUrl || url).trim(),
      name: String(img.name || '').trim(),
    });
  };
  (Array.isArray(data?.detailImages) ? data.detailImages : []).forEach(push);
  (Array.isArray(data?.mainImages) ? data.mainImages : []).forEach(push);
  (Array.isArray(data?.compositeImages) ? data.compositeImages : []).forEach(push);
  return list;
}

/** 同一次导出内相同标题+语种复用译文，避免重复请求 DeepSeek */
const exportTitleTranslateCache = new Map();

/**
 * 延迟取翻译 API，避免 exportUtils ↔ downloadCenter ↔ api 循环依赖导致
 * listingExportTranslateAPI 在模块初始化时为 undefined。
 */
function getListingExportTranslateAPI() {
  // eslint-disable-next-line global-require
  const { listingExportTranslateAPI } = require('./api');
  if (!listingExportTranslateAPI || typeof listingExportTranslateAPI.translate !== 'function') {
    throw new Error('翻译接口未加载，请刷新页面后重试');
  }
  return listingExportTranslateAPI;
}

/**
 * 将图片URL转换为服务器存储的URL（如果是blob或data URL，先保存到服务器）
 * @param {string} imageUrl - 图片URL（可能是blob:、data:或http://）
 * @returns {Promise<string>} - 服务器URL（如 /uploads/xxx.jpg）
 */
const convertImageToServerUrl = async (imageUrl) => {
  // 如果已经是服务器URL（以/uploads/开头），直接返回
  if (imageUrl.startsWith('/uploads/') || imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    // 如果是完整URL，提取路径部分
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      try {
        const url = new URL(imageUrl);
        if (url.pathname.startsWith('/uploads/')) {
          return url.pathname;
        }
      } catch (e) {
        // URL解析失败，返回原URL
      }
    }
    return imageUrl;
  }

  // 如果是data URL，直接保存
  if (imageUrl.startsWith('data:')) {
    try {

      const result = await imageStorageAPI.save(imageUrl, { folder: 'image-space' });
      if (result && result.url) {

        return result.url;
      }
    } catch (error) {
      console.error('[图片存储] 保存data URL失败:', error);
      return '';
    }
  }

  // 如果是blob URL，先转换为data URL再保存
  if (imageUrl.startsWith('blob:')) {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      
      const result = await imageStorageAPI.save(dataUrl, { folder: 'image-space' });
      if (result && result.url) {
        return result.url;
      }
    } catch (error) {
      console.error('[图片存储] 转换blob URL失败:', error);
      return '';
    }
  }

  return '';
};

/**
 * 根据平台获取对应的工作表索引
 * @param {string} platform - 平台名称
 * @returns {number} - 工作表索引
 */
const getSheetIndexByPlatform = (platform) => {
  const platformMap = {
    'SHEIN': 1,
    'SHEIN半托': 1,
    'TEMU': 2,
    'TEMU半托': 2,
    '店小秘': 0,
    'DXM': 0,
    'TikTok': 0,
    'TIKTOK': 0,
  };
  return platformMap[platform] || 0;
};

/**
 * 根据平台获取表头行索引
 * @param {string} platform - 平台名称
 * @returns {number} - 表头行索引（从0开始）
 */
const getHeaderRowIndexByPlatform = (platform) => {
  const headerRowMap = {
    'SHEIN': 1, // SHEIN第二行是表头（跳过第一行说明）
    'SHEIN半托': 1,
    'TEMU': 3, // TEMU第四行是表头
    'TEMU半托': 3,
    '店小秘': 0, // 店小秘第一行是表头
    'DXM': 0,
    'TikTok': 0,
    'TIKTOK': 0,
  };
  return headerRowMap[platform] || 0;
};

/**
 * 根据平台获取数据开始行索引
 * @param {string} platform - 平台名称
 * @returns {number} - 数据开始行索引（从0开始）
 */
const getDataStartRowIndexByPlatform = (platform) => {
  const dataStartRowMap = {
    'SHEIN': 6, // SHEIN从第7行开始填充数据
    'SHEIN半托': 6,
    'TEMU': 5, // TEMU从第6行开始填充数据
    'TEMU半托': 5,
    '店小秘': 1, // 店小秘从第2行开始填充数据
    'DXM': 1,
    'TikTok': 1,
    'TIKTOK': 1,
  };
  return dataStartRowMap[platform] || 1;
};

const SHEIN_REFERENCE_SHEET_NAMES = new Set([
  '操作指引',
  '有效属性值',
  '有效品牌列表',
  '有效产地列表',
  '尺寸表模板',
]);

const cellValueToSearchString = (value) => {
  if (value == null) return '';
  if (typeof value === 'object') {
    if (value.text) return String(value.text);
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text || '').join('');
    }
    if (value.hyperlink) return String(value.hyperlink);
  }
  return String(value);
};

/** dynamicValueConfigs 键前缀：temu-table2-12 → temu-table2（不能只用 split('-')[0]） */
const getDynamicConfigKeyPrefix = (refKey) => {
  const parts = String(refKey || '').split('-');
  if (parts.length >= 2 && /^\d+$/.test(parts[parts.length - 1])) {
    return parts.slice(0, -1).join('-');
  }
  return parts[0] || 'shein';
};

/** 解析导出应写入的主工作表（避免写到「有效属性值」等参考表） */
const resolveExportWorksheet = (workbook, platform) => {
  const platformKey = canonicalListingTemplatePlatform(platform);
  const sheetNames = workbook.worksheets.map((ws) => ws.name);
  const mainSheetName = resolveListingTemplateMainSheetName(platformKey, sheetNames);
  if (mainSheetName) {
    const mainSheet = workbook.getWorksheet(mainSheetName);
    if (mainSheet) return mainSheet;
  }
  if (platformKey === 'SHEIN半托' || platformKey === 'SHEIN') {
    const dataSheet = workbook.worksheets.find((ws) => {
      const name = String(ws.name || '').trim();
      return name && !SHEIN_REFERENCE_SHEET_NAMES.has(name) && name !== '操作指引';
    });
    if (dataSheet) return dataSheet;
  }
  const sheetIndex = getSheetIndexByPlatform(platformKey);
  return workbook.worksheets[sheetIndex] || workbook.worksheets[0];
};

const ensureTemplateHeadersFromExcel = (templateHeaders, excelHeaderMap) => {
  if (Array.isArray(templateHeaders) && templateHeaders.length > 0) {
    return templateHeaders;
  }
  return Object.entries(excelHeaderMap || {})
    .sort((a, b) => a[1] - b[1])
    .map(([name]) => name)
    .filter(Boolean);
};

const inferDynamicConfigForHeader = (templateHeaderName, cellValue) => {
  const str = cellValueToSearchString(cellValue).trim();
  const fromCell = parseDynamicPlaceholderLine(str);
  if (fromCell) return fromCell;
  const h = String(templateHeaderName || '');
  if (/印花/.test(h)) return { leftValue: '印花图', rightValue: '' };

  // 主图列名 → 默认绑导出预览 mainImages（首图不自动推断，按模板里选的「主图/结果图」为准）
  const mainIndexMatch = h.match(/主图\s*(\d+)/i);
  if (mainIndexMatch) {
    return { leftValue: '主图', rightValue: `第${mainIndexMatch[1]}张` };
  }
  if (/^主图\s*$/i.test(h.trim())) {
    return { leftValue: '主图', rightValue: '第1张' };
  }

  const detailIndexMatch = h.match(
    /细节图\s*(\d+)|轮播图\s*(\d+)|商品轮播图\s*(\d+)|SKU图\s*(\d+)/i,
  );
  if (detailIndexMatch) {
    const n =
      detailIndexMatch[1] ||
      detailIndexMatch[2] ||
      detailIndexMatch[3] ||
      detailIndexMatch[4] ||
      '1';
    return { leftValue: '结果图', rightValue: `第${n}张` };
  }
  if (/效果图|商品图|轮播|附图|细节|SKU图|sku图/i.test(h)) {
    const carouselNum = h.match(/商品轮播图\s*(\d+)/i);
    if (carouselNum) {
      return { leftValue: '结果图', rightValue: `第${carouselNum[1]}张` };
    }
    const trailingNum = h.match(/(?:图|IMG|img)\s*(\d+)\s*$/i);
    if (trailingNum) {
      return { leftValue: '结果图', rightValue: `第${trailingNum[1]}张` };
    }
    const tailDigit = h.match(/(\d+)\s*$/);
    if (tailDigit && /轮播|附图|细节|SKU|sku|商品图/.test(h)) {
      return { leftValue: '结果图', rightValue: `第${tailDigit[1]}张` };
    }
    const indexMatch = h.match(/第(\d+)/);
    if (indexMatch) {
      return { leftValue: '结果图', rightValue: `第${indexMatch[1]}张` };
    }
    return null;
  }
  // 商品名称/多语言/英文名称：不自动推断，避免未配置列被默认标题填入
  return null;
};

/** 从上架模版 Excel 中定位可填充的数据行（含占位符或首行示例） */
const discoverExportableRowIndices = (worksheet, dataStartRowIndex) => {
  const indices = [];
  const maxRow = Math.min(worksheet.rowCount || 0, dataStartRowIndex + 40);
  for (let row = dataStartRowIndex + 1; row <= maxRow; row += 1) {
    const excelRow = worksheet.getRow(row);
    let score = 0;
    excelRow.eachCell({ includeEmpty: false }, (cell) => {
      const str = cellValueToSearchString(cell.value).trim();
      if (!str) return;
      if (/结果图|主图|印花图|编号配对|\bSKC\b|印花图名称|标题-|动态值/.test(str)) score += 3;
      if (str.includes('印花图名称')) score += 2;
      score += 1;
    });
    if (score >= 3) {
      indices.push(row - 1);
    } else if (indices.length > 0) {
      break;
    }
  }
  if (indices.length === 0) {
    indices.push(dataStartRowIndex);
  }
  return indices;
};

/** 将模版数据行复制到新行（多商品导出时每条占独立行） */
const copyWorksheetRowValues = (worksheet, fromRowIndex, toRowIndex) => {
  if (!worksheet || fromRowIndex === toRowIndex) return;
  const src = worksheet.getRow(fromRowIndex + 1);
  const dest = worksheet.getRow(toRowIndex + 1);
  src.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    dest.getCell(colNumber).value = cell.value;
  });
};

const shouldResolveExportCell = (mapping, templateHeaderName, currentCellValue, item) => {
  const str = cellValueToSearchString(currentCellValue).trim();
  const headerType = mapping?.headerType;
  // 卖家SKU：不因表头名强行解析，保留固定值，勿填印花名
  if (headerType === 'SKU货号' || /卖家SKU/i.test(String(templateHeaderName || ''))) {
    return {
      should: str.includes('印花图名称') || headerType === 'SKU货号',
      inferredConfig: null,
    };
  }
  // 只认单元格里的占位符；禁止仅凭「商品轮播图N」表头给空列塞结果图
  const fromCell = parseDynamicPlaceholderLine(str);
  const should =
    Boolean(fromCell) ||
    headerType === '动态值' ||
    headerType === '多个值' ||
    headerType === '货号' ||
    headerType === 'SPU货号' ||
    str.includes('印花图名称');
  return { should, inferredConfig: fromCell };
};

const isExportPlaceholderCellText = (str) => {
  const s = String(str || '').trim();
  if (!s) return false;
  return (
    Boolean(parseDynamicPlaceholderLine(s)) ||
    /结果图|主图|印花图|编号配对|\bSKC\b|标题[-_：:]|动态值|印花图名称/.test(s)
  );
};

/**
 * 按表头填充轮播/主图列（TEMU 模版里 DL–DT 等列常为空，仅有 DK 带「结果图-第1张」占位符）
 */
const fillImageColumnsByHeader = async ({
  excelRow,
  excelHeaderMap,
  item,
  platform,
  stampName,
  dynamicValueConfigs,
  imageDownloadJobs,
  templateName,
  templateRow = null,
  templateHeaders = null,
  headerMapping = null,
  rowIndex = 0,
}) => {
  if (!excelRow || !item) return;
  for (const [headerName, colNumber] of Object.entries(excelHeaderMap || {})) {
    if (headerName === '__columnsByName' || typeof colNumber !== 'number') continue;
    const carouselIndex = extractCarouselImageIndex(headerName);
    const inferred = inferDynamicConfigForHeader(headerName, '');
    if (!inferred || !isImageDynamicConfig(inferred)) continue;
    const cell = excelRow.getCell(colNumber);
    const current = cellValueToSearchString(cell.value).trim();
    if (cellLooksLikeExportImageUrl(cell.value)) continue;
    // 空轮播列：有 SKC 货号时允许按「SKC 第N张」填充；其它空列仍不擅自推断
    if (!current) {
      if (!carouselIndex) continue;
      const skcCode = String(item.skcCode || item.skcPairingCode || '').trim()
        || resolveSkcCodeFromTemplateRow(templateRow, templateHeaders, headerMapping)
        || resolveSkcCodeFromExcelRow(excelRow, excelHeaderMap);
      if (!skcCode && !collectExportDetailImages(item).length) continue;
    } else if (!isExportPlaceholderCellText(current)) {
      // 官方模版说明文案占位：轮播列可覆盖；其它列保持原样
      if (!carouselIndex) continue;
    }
    const config = carouselIndex
      ? (
        (String(item.skcCode || item.skcPairingCode || '').trim()
          || resolveSkcCodeFromTemplateRow(templateRow, templateHeaders, headerMapping)
          || resolveSkcCodeFromExcelRow(excelRow, excelHeaderMap))
          ? { leftValue: 'SKC', rightValue: `第${carouselIndex}张` }
          : inferred
      )
      : inferred;
    const mapping = {
      excelColumn: colNumber,
      templateHeaderName: headerName,
      headerType: '动态值',
      dynamicConfig: config,
      refKey: 'header-fill',
      adjustedColIndex: colNumber,
    };
    await applyDynamicValueToExportCell({
      cell,
      mapping,
      item,
      platform,
      templateHeaderName: headerName,
      stampName,
      dynamicValueConfigs,
      rowIndex,
      imageDownloadJobs,
      templateName,
      inferredConfig: config,
      templateRow,
      templateHeaders,
      headerMapping,
      excelRow,
      excelHeaderMap,
    });
  }
};

/**
 * 兜底：TEMU 中英「商品轮播图N」列若仍无 URL，按 SKC/结果图写入并同步同序号列
 */
const fillEmptyCarouselImageColumns = async ({
  excelRow,
  excelHeaderMap,
  item,
  platform,
  stampName,
  dynamicValueConfigs,
  imageDownloadJobs,
  templateName,
  rowIndex = 0,
  templateRow = null,
  templateHeaders = null,
  headerMapping = null,
}) => {
  if (!excelRow || !item) return;
  const columnsByName = excelHeaderMap?.__columnsByName || {};
  const seenIndexes = new Set();
  const headerEntries = Object.keys(columnsByName).length > 0
    ? Object.entries(columnsByName)
    : Object.entries(excelHeaderMap || {})
      .filter(([name, col]) => name !== '__columnsByName' && typeof col === 'number')
      .map(([name, col]) => [name, [col]]);

  for (const [headerName, cols] of headerEntries) {
    const n = extractCarouselImageIndex(headerName);
    if (!n || seenIndexes.has(n)) continue;
    seenIndexes.add(n);
    const colList = Array.isArray(cols) ? cols : [cols].filter((c) => typeof c === 'number');
    const primaryCol = colList[0];
    if (!primaryCol) continue;
    const anyFilled = colList.some((col) => cellLooksLikeExportImageUrl(excelRow.getCell(col).value));
    if (anyFilled) continue;

    const resolvedSkc = String(item.skcCode || item.skcPairingCode || '').trim()
      || resolveSkcCodeFromTemplateRow(templateRow, templateHeaders, headerMapping)
      || resolveSkcCodeFromExcelRow(excelRow, excelHeaderMap);
    const configs = [
      resolvedSkc ? { leftValue: 'SKC', rightValue: `第${n}张` } : null,
      { leftValue: '结果图', rightValue: `第${n}张` },
    ].filter(Boolean);

    const cell = excelRow.getCell(primaryCol);
    for (const config of configs) {
      const mapping = {
        excelColumn: primaryCol,
        templateHeaderName: headerName,
        headerType: '动态值',
        dynamicConfig: config,
        refKey: 'carousel-empty-fill',
        adjustedColIndex: primaryCol,
      };
      await applyDynamicValueToExportCell({
        cell,
        mapping,
        item: { ...item, skcCode: resolvedSkc || item.skcCode },
        platform,
        templateHeaderName: headerName,
        stampName,
        dynamicValueConfigs,
        rowIndex,
        imageDownloadJobs,
        templateName,
        inferredConfig: config,
        templateRow,
        templateHeaders,
        headerMapping,
        excelRow,
        excelHeaderMap,
      });
      if (cellLooksLikeExportImageUrl(cell.value)) {
        console.info('[按模板导出][商品轮播图] 空列兜底写入成功', {
          header: headerName,
          index: n,
          mode: config.leftValue,
          skcCode: resolvedSkc,
          excelColumn: primaryCol,
        });
        break;
      }
    }
  }
};

/** 按行扫描「结果图-第N张」等占位符并替换为图片 URL（不依赖表头映射，兼容 TEMU 多列同名表头） */
const replacePlaceholdersInExcelRow = async ({
  excelRow,
  excelHeaderMap,
  item,
  platform,
  stampName,
  dynamicValueConfigs,
  imageDownloadJobs,
  templateName,
  rowIndex = 0,
  templateRow = null,
  templateHeaders = null,
  headerMapping = null,
}) => {
  if (!excelRow || !item) return;
  const worksheet = excelRow.worksheet;
  // columnCount 可能偏小，按表头映射最大列扫到轮播图等靠后列
  const headerMaxCol = Object.values(excelHeaderMap || {}).reduce(
    (max, col) => Math.max(max, Number(col) || 0),
    0,
  );
  const maxCol = Math.min(Math.max(worksheet?.columnCount || 0, headerMaxCol, 1), 400);

  for (let colNumber = 1; colNumber <= maxCol; colNumber += 1) {
    const cell = excelRow.getCell(colNumber);
    const currentCellValue = cell.value ?? '';
    const str = cellValueToSearchString(currentCellValue).trim();
    if (!isExportPlaceholderCellText(str)) continue;

    const placeholderConfig = parseDynamicPlaceholderLine(str);
    if (!placeholderConfig && str.includes('印花图名称')) {
      continue;
    }
    if (!placeholderConfig) continue;

    let templateHeaderName = '';
    for (const [headerName, colIndex] of Object.entries(excelHeaderMap || {})) {
      if (headerName === '__columnsByName') continue;
      if (colIndex === colNumber) {
        templateHeaderName = headerName;
        break;
      }
    }

    const mapping = {
      excelColumn: colNumber,
      templateHeaderName,
      headerType: '动态值',
      dynamicConfig: placeholderConfig,
      refKey: 'export-scan',
      adjustedColIndex: colNumber,
    };

    await applyDynamicValueToExportCell({
      cell,
      mapping,
      item,
      platform,
      templateHeaderName,
      stampName,
      dynamicValueConfigs,
      rowIndex,
      imageDownloadJobs,
      templateName,
      inferredConfig: placeholderConfig,
      templateRow,
      templateHeaders,
      headerMapping,
      excelRow,
      excelHeaderMap,
    });
  }
  await fillImageColumnsByHeader({
    excelRow,
    excelHeaderMap,
    item,
    platform,
    stampName,
    dynamicValueConfigs,
    imageDownloadJobs,
    templateName,
    templateRow,
    templateHeaders,
    headerMapping,
    rowIndex,
  });
  await fillEmptyCarouselImageColumns({
    excelRow,
    excelHeaderMap,
    item,
    platform,
    stampName,
    dynamicValueConfigs,
    imageDownloadJobs,
    templateName,
    rowIndex,
    templateRow,
    templateHeaders,
    headerMapping,
  });
};

/** TEMU：把 table1（基本信息+商品属性）模板值写入某一 Excel 行（随商品信息行数铺开） */
function writeTemuTable1SharedColumnsToExcelRow({
  excelRow,
  templateRowTable1,
  templateHeadersTable1,
  headerMappingTable1,
  dynamicValueConfigs,
  skipProductLevel = true,
}) {
  if (!excelRow || !Array.isArray(templateRowTable1) || !Array.isArray(templateHeadersTable1)) {
    return;
  }
  templateRowTable1.forEach((cellValue, templateColIndex) => {
    if (cellValue === '__ADD__' || cellValue === '__OPERATION__') return;
    const header = templateHeadersTable1[templateColIndex];
    const templateHeaderName = typeof header === 'string'
      ? header
      : (header?.name || header?.displayName || '');
    if (
      templateHeaderName === '+'
      || templateHeaderName === '操作'
      || templateHeaderName === '__ADD__'
      || templateHeaderName === '__OPERATION__'
    ) {
      return;
    }
    const mapping = headerMappingTable1?.[templateColIndex];
    if (!mapping) return;

    if (skipProductLevel && isTemuProductLevelHeaderName(templateHeaderName)) return;

    const cell = excelRow.getCell(mapping.excelColumn);
    let finalCellValue = '';
    if (mapping.headerType === 'SKU货号') {
      finalCellValue = String(cellValue || '');
    } else if (mapping.headerType === 'SPU货号') {
      finalCellValue = `${String(cellValue || '')}印花图名称`;
    } else {
      const rowDynamicConfig = resolveDynamicConfigForMapping(
        mapping,
        0,
        dynamicValueConfigs,
      );
      if (mapping.headerType === '动态值' && rowDynamicConfig) {
        const { leftValue, rightValue } = rowDynamicConfig;
        if (leftValue === '印花图' || leftValue === '名称') {
          finalCellValue = leftValue;
        } else if (leftValue === '编号配对') {
          finalCellValue = `编号配对-${rightValue || '第1张'}`;
        } else if (leftValue && rightValue) {
          if (leftValue === '标题' && rightValue !== '原标题') {
            finalCellValue = `标题-${rightValue}`;
          } else if (leftValue === '结果图' && rightValue) {
            finalCellValue = `结果图-${rightValue}`;
          } else {
            finalCellValue = `${leftValue}-${rightValue}`;
          }
        } else {
          finalCellValue = cellValue || '动态值';
        }
      } else if (mapping.headerType === '多个值') {
        finalCellValue = cellValue || '多个值';
      } else {
        finalCellValue = cellValue || '';
      }
    }
    if (finalCellValue !== undefined && finalCellValue !== null && finalCellValue !== '') {
      cell.value = finalCellValue;
    }
  });
}

function resolveSkuCodeFromTemplateRow(templateRow, templateHeaders, headerMapping) {
  if (!Array.isArray(templateRow) || !Array.isArray(templateHeaders)) return '';
  for (let i = 0; i < templateHeaders.length; i += 1) {
    const header = templateHeaders[i];
    const name = typeof header === 'string' ? header : (header?.name || header?.displayName || '');
    const mapping = headerMapping?.[i];
    const isSku = mapping?.headerType === 'SKU货号'
      || /SKU货号|卖家SKU/i.test(String(name || ''));
    if (!isSku) continue;
    const val = templateRow[i];
    if (val === '__ADD__' || val === '__OPERATION__') continue;
    const text = String(val ?? '').trim().replace(/印花图名称/g, '').trim();
    if (text) return text;
  }
  return '';
}

function resolveSkuCodeFromExcelRow(excelRow, excelHeaderMap) {
  if (!excelRow || !excelHeaderMap) return '';
  for (const [headerName, colIndex] of Object.entries(excelHeaderMap)) {
    if (headerName === '__columnsByName') continue;
    if (!/SKU货号|卖家SKU/i.test(String(headerName || ''))) continue;
    if (typeof colIndex !== 'number') continue;
    const cell = excelRow.getCell(colIndex);
    const text = cellValueToSearchString(cell?.value).trim();
    if (text) return text;
  }
  return '';
}

function isSkcHeaderName(name, headerType) {
  if (headerType === 'SKC货号') return true;
  const text = String(name || '').trim();
  if (!text) return false;
  // 兼容「SKC货号」「SKC编码」「SKC」等表头
  if (/^SKC$/i.test(text)) return true;
  return /SKC货号|SKC编码|SKC\s*货号/i.test(text);
}

function resolveSkcCodeFromTemplateRow(templateRow, templateHeaders, headerMapping) {
  if (!Array.isArray(templateRow) || !Array.isArray(templateHeaders)) return '';
  for (let i = 0; i < templateHeaders.length; i += 1) {
    const header = templateHeaders[i];
    const name = typeof header === 'string' ? header : (header?.name || header?.displayName || '');
    const mapping = headerMapping?.[i];
    if (!isSkcHeaderName(name, mapping?.headerType)) continue;
    const val = templateRow[i];
    if (val === '__ADD__' || val === '__OPERATION__') continue;
    const text = String(val ?? '').trim().replace(/印花图名称/g, '').trim();
    if (text) return text;
  }
  return '';
}

function resolveSkcCodeFromExcelRow(excelRow, excelHeaderMap) {
  if (!excelRow || !excelHeaderMap) return '';
  for (const [headerName, colIndex] of Object.entries(excelHeaderMap)) {
    if (headerName === '__columnsByName') continue;
    if (!isSkcHeaderName(headerName)) continue;
    if (typeof colIndex !== 'number') continue;
    const cell = excelRow.getCell(colIndex);
    const text = cellValueToSearchString(cell?.value).trim();
    if (text) return text;
  }
  return '';
}

/**
 * 获取动态值（根据配置从数据中提取）
 * @param {Object} config - 动态值配置 { leftValue, rightValue }
 * @param {Object} data - 导出数据项
 * @param {string} prefix - 印花图名称前缀（如"5000-W-"）
 * @returns {Promise<string>} - 动态值
 */
const getDynamicValue = async (config, data, prefix = '') => {
  if (!config || !config.leftValue) {
    return '';
  }

  const { leftValue, rightValue } = config;

  // 印花图：返回原图 URL，由写入阶段统一换公网链（与结果图/编号配对一致）
  if (leftValue === '印花图') {
    const url = String(data.stampImageUrl || '').trim();
    if (url) {
      console.info('[按模板导出][印花图] 取图成功', {
        header: data.exportHeaderName || '',
        row: data.exportRowIndex,
        urlPreview: url.slice(0, 80),
      });
    } else {
      console.warn('[按模板导出][印花图] 取图失败/跳过', {
        header: data.exportHeaderName || '',
        row: data.exportRowIndex,
        reason: 'stamp_image_empty',
      });
    }
    return url;
  }

  // 编号配对：取组内第 N 张图，文件名与 SKU 提取键精确匹配后返回原图 URL（由导出阶段换公网链）
  // 无 SKU 时回退 SKC货号（兼容只填了 SKC=1001-B、图名 1001-B-1 的场景）
  if (leftValue === '编号配对') {
    const skuCode = String(data.skuCode || data.skuPairingCode || '').trim();
    const skcCode = String(data.skcCode || data.skcPairingCode || '').trim();
    const detailImages = collectExportDetailImages(data);
    let diag = diagnoseNthDetailImagePairing(
      detailImages,
      skuCode || skcCode,
      rightValue || '第1张',
    );
    // SKU 三段规则仍失败时，再用完整 SKC 子组规则（图名去掉末段序号后对齐）
    if (!diag.ok && skcCode && skcCode !== skuCode) {
      const skcDiag = diagnoseNthDetailImagePairingBySkc(
        detailImages,
        skcCode,
        rightValue || '第1张',
      );
      if (skcDiag.ok) {
        diag = {
          ...skcDiag,
          skuCode: skcCode,
          skuKey: skcDiag.skcKey,
          reason: `skc_fallback:${skcDiag.reason}`,
        };
      }
    }
    const imageNames = detailImages
      .slice(0, 12)
      .map((img, i) => `#${i + 1}:${img?.name || '(无名称)'}`);
    if (diag.ok) {
      console.info('[按模板导出][编号配对] 匹配成功', {
        header: data.exportHeaderName || '',
        row: data.exportRowIndex,
        skuCode: diag.skuCode,
        skuKey: diag.skuKey,
        skcCode,
        rightValue: diag.rightValue,
        path: diag.path,
        reason: diag.reason,
        imageName: diag.image?.name,
        urlPreview: String(diag.image?.url || diag.image?.previewUrl || '').slice(0, 80),
        detailCount: diag.detailCount,
      });
    } else {
      console.warn('[按模板导出][编号配对] 匹配失败/跳过', {
        header: data.exportHeaderName || '',
        row: data.exportRowIndex,
        skuCode: diag.skuCode || skuCode,
        skuKey: diag.skuKey,
        skcCode,
        rightValue: diag.rightValue,
        reason: diag.reason,
        index: diag.index,
        absoluteName: diag.absoluteName,
        absoluteKey: diag.absoluteKey,
        matchedCount: diag.matchedCount,
        detailCount: diag.detailCount,
        detailNames: imageNames,
      });
    }
    return String(diag.image?.url || diag.image?.previewUrl || '').trim();
  }

  // SKC：先按图名匹配 SKC货号得到一组图，再按「第N张」取组内位置（同结果图）
  if (leftValue === 'SKC') {
    const detailImages = collectExportDetailImages(data);
    const diag = diagnoseNthDetailImagePairingBySkc(
      detailImages,
      data.skcCode || data.skcPairingCode || '',
      rightValue || '第1张',
    );
    const imageNames = detailImages
      .slice(0, 12)
      .map((img, i) => `#${i + 1}:${img?.name || '(无名称)'}`);
    if (diag.ok) {
      console.info('[按模板导出][SKC] 匹配成功', {
        header: data.exportHeaderName || '',
        row: data.exportRowIndex,
        skcCode: diag.skcCode,
        skcKey: diag.skcKey,
        rightValue: diag.rightValue,
        path: diag.path,
        reason: diag.reason,
        imageName: diag.image?.name,
        urlPreview: String(diag.image?.url || diag.image?.previewUrl || '').slice(0, 80),
        detailCount: diag.detailCount,
      });
    } else {
      console.warn('[按模板导出][SKC] 匹配失败/跳过', {
        header: data.exportHeaderName || '',
        row: data.exportRowIndex,
        skcCode: diag.skcCode,
        skcKey: diag.skcKey,
        rightValue: diag.rightValue,
        reason: diag.reason,
        index: diag.index,
        absoluteName: diag.absoluteName,
        absoluteKey: diag.absoluteKey,
        matchedCount: diag.matchedCount,
        detailCount: diag.detailCount,
        detailNames: imageNames,
      });
    }
    return String(diag.image?.url || diag.image?.previewUrl || '').trim();
  }

  if (!rightValue) {
    return '';
  }
  // 处理标题：按模板语种导出；源语种不一致（或正文不像目标语）时 DeepSeek 翻译，不改原标题
  if (leftValue === '标题' && rightValue) {
    const title = data.title || '';
    if (!title) {
      return '';
    }

    // 「原标题」：始终输出原文
    if (rightValue === '原标题' || String(rightValue).includes('原标题')) {
      return title;
    }

    const targetLang = resolveExportTitleTargetLanguage(
      rightValue,
      data.exportHeaderName || data.templateHeaderName || '',
    );
    if (!targetLang) {
      return title;
    }

    const sourceLang = data.titleLanguage || data.language || '';
    if (!shouldTranslateExportTitle(sourceLang, targetLang, title)) {
      return title;
    }

    const cacheKey = `${targetLang}::${sourceLang || ''}::${title}`;
    if (exportTitleTranslateCache.has(cacheKey)) {
      return exportTitleTranslateCache.get(cacheKey);
    }

    try {
      const result = await getListingExportTranslateAPI().translate(title, targetLang, sourceLang);
      const translatedText = String(
        result?.translatedText || result?.data?.translatedText || '',
      ).trim();
      if (!translatedText) {
        throw new Error('翻译接口未返回译文');
      }
      // 译文仍明显不像目标语时视为失败，避免把中文原样写入 [en] 列
      if (!textLooksLikeLanguage(translatedText, targetLang) && textLooksLikeLanguage(title, 'zh')) {
        throw new Error(`译文仍不像目标语种 ${targetLang}`);
      }
      exportTitleTranslateCache.set(cacheKey, translatedText);
      return translatedText;
    } catch (error) {
      const msg = error?.message || String(error);
      throw new Error(`商品标题翻译为「${rightValue}」失败：${msg}`);
    }
  }

  // 结果图：按「第N张」从细节/主图/成品合并列表取序（不做编号配对文件名匹配）
  if (leftValue === '结果图' && rightValue) {
    const match = String(rightValue).match(/第\s*(\d+)\s*张/) || String(rightValue).match(/^(\d+)$/);
    if (!match) return '';
    const index = parseInt(match[1], 10) - 1;
    const images = collectExportDetailImages(data);
    const picked = images[index];
    return String(picked?.url || picked?.previewUrl || '').trim();
  }

  // 主图：按「第N张」从 mainImages 取序
  if (leftValue === '主图' && rightValue) {
    const match = String(rightValue).match(/第\s*(\d+)\s*张/) || String(rightValue).match(/^(\d+)$/);
    if (!match) return '';
    const index = parseInt(match[1], 10) - 1;
    const images = Array.isArray(data.mainImages) ? data.mainImages : [];
    const picked = images[index];
    return String(picked?.url || picked?.previewUrl || '').trim();
  }

  // 处理名称（添加前缀）；仅使用关联 AI 文案，无关联时「无标题」（与导出预览一致，不用印花名）
  if (leftValue === '名称') {
    const name = String(data.title || '').trim() || '无标题';
    const result = prefix && name !== '无标题' ? `${prefix}${name}` : name;
    return result;
  }
  return '';
};

/** 从单元格占位文本解析动态值配置（兼容「结果图-第1张」「结果图第1张」等） */
const parseDynamicPlaceholderLine = (line) => {
  const trimmed = String(line || '').trim();
  if (!trimmed) return null;

  const mainMatch = trimmed.match(/主图[-_]?第\s*(\d+)\s*张/);
  if (mainMatch) {
    return { leftValue: '主图', rightValue: `第${mainMatch[1]}张` };
  }
  if (trimmed === '主图') {
    return { leftValue: '主图', rightValue: '第1张' };
  }
  const resultMatch = trimmed.match(/结果图[-_]?第\s*(\d+)\s*张/);
  if (resultMatch) {
    return { leftValue: '结果图', rightValue: `第${resultMatch[1]}张` };
  }
  const onlyIndexMatch = trimmed.match(/^第\s*(\d+)\s*张$/);
  if (onlyIndexMatch) {
    return { leftValue: '结果图', rightValue: `第${onlyIndexMatch[1]}张` };
  }
  if (trimmed.startsWith('标题-') || trimmed.startsWith('标题：') || trimmed.startsWith('标题:')) {
    const rightValue = trimmed.replace(/^标题[-_：:]+/, '').trim();
    return { leftValue: '标题', rightValue: rightValue || '原标题' };
  }
  if (trimmed === '印花图' || trimmed.startsWith('印花图')) {
    return { leftValue: '印花图', rightValue: '' };
  }
  const pairingMatch = trimmed.match(/编号配对[-_]?第\s*(\d+)\s*张/);
  if (pairingMatch) {
    return { leftValue: '编号配对', rightValue: `第${pairingMatch[1]}张` };
  }
  if (trimmed === '编号配对' || trimmed.startsWith('编号配对')) {
    return { leftValue: '编号配对', rightValue: '第1张' };
  }
  const skcMatch = trimmed.match(/^SKC[-_]?第\s*(\d+)\s*张$/i);
  if (skcMatch) {
    return { leftValue: 'SKC', rightValue: `第${skcMatch[1]}张` };
  }
  if (/^SKC$/i.test(trimmed) || /^SKC[-_]/i.test(trimmed)) {
    return { leftValue: 'SKC', rightValue: '第1张' };
  }
  if (trimmed === '名称' || trimmed.startsWith('名称')) {
    return { leftValue: '名称', rightValue: '' };
  }
  return null;
};

const resolveDynamicConfigForMapping = (mapping, rowIndex, dynamicValueConfigs) => {
  if (!mapping) return null;
  if (!dynamicValueConfigs) return mapping.dynamicConfig || null;

  const prefix = getDynamicConfigKeyPrefix(mapping.refKey);
  const colLevelKey = `${prefix}-${mapping.adjustedColIndex}`;
  const rowLevelKey = `${prefix}-${mapping.adjustedColIndex}-${rowIndex}`;

  // 行级配置优先（同一列多行可分别选主图/结果图），再列级，最后 mapping 默认值
  if (dynamicValueConfigs[rowLevelKey]) return dynamicValueConfigs[rowLevelKey];
  if (dynamicValueConfigs[colLevelKey]) return dynamicValueConfigs[colLevelKey];
  if (mapping.dynamicConfig) return mapping.dynamicConfig;

  if (rowIndex == null || Number.isNaN(Number(rowIndex))) {
    for (let i = 0; i < 100; i += 1) {
      const testKey = `${prefix}-${mapping.adjustedColIndex}-${i}`;
      if (dynamicValueConfigs[testKey]) return dynamicValueConfigs[testKey];
    }
  }
  return null;
};

const normalizeExportedImageValue = async (dynamicValue, dynamicConfig) => {
  if (!dynamicValue) return '';
  if (
    dynamicConfig?.leftValue === '结果图' ||
    dynamicConfig?.leftValue === '主图' ||
    dynamicConfig?.leftValue === '印花图' ||
    dynamicConfig?.leftValue === '编号配对' ||
    dynamicConfig?.leftValue === 'SKC'
  ) {
    try {
      const serverUrl = await convertImageToServerUrl(dynamicValue);
      if (serverUrl) {
        const href = await toExportPublicUrl(serverUrl);
        if (href) return href;
      }
    } catch (error) {
      console.error('[导出] 图片转存失败:', error);
    }
    return await toExportPublicUrl(dynamicValue);
  }
  return dynamicValue;
};

const isImageDynamicConfig = (config) =>
  config?.leftValue === '结果图'
  || config?.leftValue === '主图'
  || config?.leftValue === '印花图'
  || config?.leftValue === '编号配对'
  || config?.leftValue === 'SKC';

const sanitizeExportFileName = (name) =>
  String(name || 'image').replace(/[\\/:*?"<>|]+/g, '_').trim() || 'image';

const buildExportImageFileName = (item, dynamicConfig) => {
  const stamp = sanitizeExportFileName(item?.stampName || '图案');
  if (dynamicConfig?.leftValue === '印花图') return `${stamp}_印花.jpg`;
  if (dynamicConfig?.leftValue === '编号配对') {
    const key = extractSkuPairingKey(item?.skuCode || item?.skuPairingCode || '')
      || sanitizeExportFileName(item?.stampName || '配对');
    const match = String(dynamicConfig?.rightValue || '').match(/第(\d+)张/);
    const index = match ? match[1] : '1';
    return `${stamp}_${key}_配对${index}.jpg`;
  }
  if (dynamicConfig?.leftValue === 'SKC') {
    const key = extractSkcPairingKey(item?.skcCode || item?.skcPairingCode || '')
      || sanitizeExportFileName(item?.stampName || 'SKC');
    const match = String(dynamicConfig?.rightValue || '').match(/第(\d+)张/);
    const index = match ? match[1] : '1';
    return `${stamp}_${key}_SKC${index}.jpg`;
  }
  const match = String(dynamicConfig?.rightValue || '').match(/第(\d+)张/);
  const index = match ? match[1] : '1';
  if (dynamicConfig?.leftValue === '主图') return `${stamp}_主图${index}.jpg`;
  return `${stamp}_详情图${index}.jpg`;
};

const normalizeExportDownloadUrlKey = (url) => {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
  try {
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      const parsed = new URL(raw);
      return parsed.pathname + parsed.search;
    }
  } catch {
    /* ignore */
  }
  return raw.split('?')[0];
};

/** 仅下载主图/结果图（不下载印花图）；每张成品图只下载一次 */
const shouldQueueImageDownload = (item, dynamicConfig) => {
  if (!dynamicConfig) return false;
  if (dynamicConfig.leftValue === '印花图') return false;
  if (dynamicConfig.leftValue === '编号配对') {
    return Boolean(matchNthDetailImageBySkuKey(
      collectExportDetailImages(item),
      item?.skuCode || item?.skuPairingCode || '',
      dynamicConfig.rightValue || '第1张',
    )?.url);
  }
  if (dynamicConfig.leftValue === 'SKC') {
    return Boolean(matchNthDetailImageBySkcKey(
      collectExportDetailImages(item),
      item?.skcCode || item?.skcPairingCode || '',
      dynamicConfig.rightValue || '第1张',
    )?.url);
  }
  if (dynamicConfig.leftValue === '结果图') {
    const match = String(dynamicConfig.rightValue || '').match(/第(\d+)张/);
    const index = match ? parseInt(match[1], 10) - 1 : 0;
    const count = collectExportDetailImages(item).length;
    return index < count;
  }
  if (dynamicConfig.leftValue === '主图') {
    const match = String(dynamicConfig.rightValue || '').match(/第(\d+)张/);
    const index = match ? parseInt(match[1], 10) - 1 : 0;
    const count = Array.isArray(item?.mainImages) ? item.mainImages.length : 0;
    return index < count;
  }
  return false;
};

const buildImageDownloadJobKey = (job) => {
  const stamp = String(job?.meta?.stampName || '').trim();
  const nameKey = String(job?.fileName || '').trim();
  if (stamp && nameKey) return `${stamp}::${nameKey}`;
  return normalizeExportDownloadUrlKey(job?.url);
};

const registerImageDownloadJob = (imageDownloadJobs, job) => {
  if (!imageDownloadJobs?.list || !job?.url) return;
  const key = buildImageDownloadJobKey(job);
  if (!key || imageDownloadJobs.seen.has(key)) return;
  imageDownloadJobs.seen.add(key);
  imageDownloadJobs.list.push(job);
};

const resolveImageHrefForExportCell = async (dynamicValue, dynamicConfig) => {
  const raw = String(dynamicValue || '').trim();
  if (!raw) return '';
  const normalized = await normalizeExportedImageValue(raw, dynamicConfig);
  const candidates = [normalized, raw].filter(Boolean);
  for (const candidate of candidates) {
    if (isAlreadyPublicExportUrl(candidate)) return candidate;
    const href = await toExportPublicUrl(candidate);
    if (href && !href.startsWith('blob:') && !href.startsWith('data:')) return href;
    if (href && isAlreadyPublicExportUrl(href)) return href;
  }
  // 仍无公网链时：若原文已是 http(s) 且非受管私链形态，允许写入（优于空白）
  if (isAlreadyPublicExportUrl(raw)) return raw;
  return '';
};

const writeValueToSameNamedExcelColumns = (excelRow, templateHeaderName, columnsByName, primaryCol, value) => {
  if (!excelRow || !templateHeaderName) return;
  const siblings = columnsByName?.[templateHeaderName];
  if (!Array.isArray(siblings) || siblings.length === 0) return;
  siblings.forEach((col) => {
    if (primaryCol && col === primaryCol) return;
    excelRow.getCell(col).value = value;
  });
  if (siblings.length > 1) {
    const preview = typeof value === 'string' ? value.slice(0, 80) : (value?.hyperlink || '');
    if (preview) {
      console.info('[导出] 同名列表头已同步写入', {
        header: templateHeaderName,
        columns: siblings,
        primaryCol,
      });
    }
  }
};

/** TEMU 中英「商品轮播图N」列名不一致时，按序号同步写入，避免只写到英列、中文 DK 仍空 */
const writeCarouselColumnsByIndex = (excelRow, templateHeaderName, columnsByName, primaryCol, value) => {
  writeValueToSameNamedExcelColumns(
    excelRow,
    templateHeaderName,
    columnsByName,
    primaryCol,
    value,
  );
  const n = extractCarouselImageIndex(templateHeaderName);
  if (!n || !excelRow || !columnsByName) return;
  const synced = [];
  Object.entries(columnsByName).forEach(([name, cols]) => {
    if (extractCarouselImageIndex(name) !== n) return;
    (Array.isArray(cols) ? cols : []).forEach((col) => {
      if (primaryCol && col === primaryCol) return;
      excelRow.getCell(col).value = value;
      synced.push({ name, col });
    });
  });
  if (synced.length > 0) {
    console.info('[导出] 商品轮播图同序号列已同步写入', {
      header: templateHeaderName,
      index: n,
      synced,
      primaryCol,
    });
  }
};

const cellLooksLikeExportImageUrl = (value) => {
  const text = cellValueToSearchString(value).trim();
  if (!text) return false;
  if (/^https?:\/\//i.test(text) || text.startsWith('/uploads/')) return true;
  if (value && typeof value === 'object' && (value.hyperlink || value.text)) {
    const href = String(value.hyperlink || value.text || '').trim();
    return /^https?:\/\//i.test(href) || href.startsWith('/uploads/');
  }
  return false;
};

const assignCellExportValue = async (cell, dynamicValue, dynamicConfig, ctx) => {
  if (isImageDynamicConfig(dynamicConfig)) {
    const kind = dynamicConfig.leftValue || '图片';
    const writeImageColumns = (value) => {
      if (extractCarouselImageIndex(ctx.templateHeaderName)) {
        writeCarouselColumnsByIndex(
          ctx.excelRow,
          ctx.templateHeaderName,
          ctx.excelHeaderColumnsByName,
          ctx.excelColumn,
          value,
        );
        return;
      }
      writeValueToSameNamedExcelColumns(
        ctx.excelRow,
        ctx.templateHeaderName,
        ctx.excelHeaderColumnsByName,
        ctx.excelColumn,
        value,
      );
    };
    if (!dynamicValue) {
      cell.value = '';
      writeImageColumns('');
      console.warn(`[按模板导出][${kind}] 写入跳过：无匹配 URL`, {
        header: ctx.templateHeaderName,
        row: ctx.item?.exportRowIndex,
        excelColumn: ctx.excelColumn,
        skcCode: ctx.item?.skcCode || '',
        skuCode: ctx.item?.skuCode || '',
        detailNames: collectExportDetailImages(ctx.item)
          .slice(0, 12)
          .map((img, i) => `#${i + 1}:${img?.name || '(无名称)'}`),
      });
      return;
    }
    const href = await resolveImageHrefForExportCell(dynamicValue, dynamicConfig);
    if (!href) {
      // 换链失败时清空占位符，避免把「结果图-第N张」等原文写入 Excel
      cell.value = '';
      writeImageColumns('');
      console.warn(`[按模板导出][${kind}] 写入跳过：公网链换取失败`, {
        header: ctx.templateHeaderName,
        row: ctx.item?.exportRowIndex,
        excelColumn: ctx.excelColumn,
        skcCode: ctx.item?.skcCode || '',
        skuCode: ctx.item?.skuCode || '',
        rawPreview: String(dynamicValue).slice(0, 100),
        detailNames: collectExportDetailImages(ctx.item)
          .slice(0, 12)
          .map((img, i) => `#${i + 1}:${img?.name || '(无名称)'}`),
      });
      return;
    }
    const fileName = buildExportImageFileName(ctx.item, dynamicConfig);
    const label =
      dynamicConfig?.leftValue === '印花图'
        ? '印花图'
        : dynamicConfig?.leftValue === '编号配对'
          ? (dynamicConfig?.rightValue ? `编号配对-${dynamicConfig.rightValue}` : '编号配对')
          : dynamicConfig?.leftValue === 'SKC'
            ? (dynamicConfig?.rightValue ? `SKC-${dynamicConfig.rightValue}` : 'SKC')
          : dynamicConfig?.leftValue === '主图'
            ? (dynamicConfig?.rightValue || '主图')
            : dynamicConfig?.rightValue || '结果图';
    const platformKey = canonicalListingTemplatePlatform(ctx.platform);
    // 平台导入需要可访问的图片 URL；单元格直接填链接，Excel/WPS 中可点击打开
    const cellValue = (
      platformKey === 'SHEIN'
      || platformKey === 'SHEIN半托'
      || platformKey === 'TEMU'
      || platformKey === 'TEMU半托'
    )
      ? href
      : {
        text: label,
        hyperlink: href,
        tooltip: fileName,
      };
    cell.value = cellValue;
    writeImageColumns(cellValue);
    console.info(`[按模板导出][${kind}] 已写入单元格`, {
      header: ctx.templateHeaderName,
      row: ctx.item?.exportRowIndex,
      excelColumn: ctx.excelColumn,
      siblingColumns: ctx.excelHeaderColumnsByName?.[ctx.templateHeaderName] || [],
      hrefPreview: String(href).slice(0, 100),
      skcCode: ctx.item?.skcCode || '',
    });
    if (shouldQueueImageDownload(ctx.item, dynamicConfig)) {
      registerImageDownloadJob(ctx.imageDownloadJobs, {
        url: href,
        fileName,
        source: '按模版导出',
        meta: {
          stampName: ctx.item?.stampName,
          templateName: ctx.templateName,
          kind: dynamicConfig?.leftValue,
        },
      });
    }
    return;
  }
  cell.value = dynamicValue;
};

const applyTemuTitleColumns = async (cell, templateHeaderName, item, dynamicConfig) => {
  const title = item.title || '';
  // 仅当模板显式配置了「标题」动态值时才填充，避免多语言/英文名称被默认商品名称填入
  if (!title || !dynamicConfig || dynamicConfig.leftValue !== '标题') return false;

  const data = { ...item, exportHeaderName: templateHeaderName };

  if (templateHeaderName === '商品名称' || templateHeaderName.includes('商品名称')) {
    const dynamicValue = await getDynamicValue(dynamicConfig, data, '');
    if (dynamicValue) cell.value = dynamicValue;
    return true;
  }

  if (templateHeaderName === '英文名称' || templateHeaderName.includes('英文名称')) {
    const dynamicValue = await getDynamicValue(dynamicConfig, data, '');
    if (dynamicValue) cell.value = dynamicValue;
    return true;
  }
  return false;
};

/**
 * 将动态值/占位符解析为真实导出内容（标题、轮播结果图、印花图、货号等）
 */
const applyDynamicValueToExportCell = async ({
  cell,
  mapping,
  item,
  platform,
  templateHeaderName,
  stampName,
  dynamicValueConfigs,
  rowIndex = 0,
  imageDownloadJobs = null,
  templateName = '',
  inferredConfig = null,
  skuCode = '',
  templateRow = null,
  templateHeaders = null,
  headerMapping = null,
  excelRow = null,
  excelHeaderMap = null,
}) => {
  const resolvedSkuCode = String(skuCode || '').trim()
    || resolveSkuCodeFromTemplateRow(templateRow, templateHeaders, headerMapping)
    || resolveSkuCodeFromExcelRow(excelRow, excelHeaderMap)
    || '';
  const resolvedSkcCode = resolveSkcCodeFromTemplateRow(templateRow, templateHeaders, headerMapping)
    || resolveSkcCodeFromExcelRow(excelRow, excelHeaderMap)
    || '';
  const assignCtx = {
    item: {
      ...item,
      exportRowIndex: rowIndex,
      skuCode: resolvedSkuCode,
      skcCode: resolvedSkcCode,
    },
    imageDownloadJobs,
    templateName,
    platform,
    excelRow,
    templateHeaderName,
    excelColumn: mapping?.excelColumn,
    excelHeaderColumnsByName: excelHeaderMap?.__columnsByName || null,
  };
  const currentCellValue = cell.value ?? '';
  const str = cellValueToSearchString(currentCellValue).trim();
  const { headerType } = mapping;

  // 卖家SKU：不填入印花名称；若残留占位符则清除
  if (headerType === 'SKU货号' || /卖家SKU/i.test(String(templateHeaderName || ''))) {
    if (str.includes('印花图名称')) {
      cell.value = String(cellValueToSearchString(currentCellValue)).replace(/印花图名称/g, '').trim();
    }
    return;
  }

  if (headerType === '货号' || headerType === 'SPU货号') {
    if (str.includes('印花图名称')) {
      cell.value = String(cellValueToSearchString(currentCellValue)).replace(/印花图名称/g, stampName);
    }
    return;
  }

  if (platform === 'TEMU半托' || platform === 'TEMU') {
    let dynamicConfig = resolveDynamicConfigForMapping(mapping, rowIndex, dynamicValueConfigs);
    const handled = await applyTemuTitleColumns(cell, templateHeaderName, item, dynamicConfig);
    if (handled) return;
  }

  // 单元格占位符优先（保证「编号配对-第N张」按单元格解析成链接，而非残留原文）
  const fromCell = parseDynamicPlaceholderLine(str);
  let dynamicConfig =
    fromCell
    || resolveDynamicConfigForMapping(mapping, rowIndex, dynamicValueConfigs)
    || inferredConfig;
  const dataForDynamic = {
    ...item,
    exportHeaderName: templateHeaderName,
    exportRowIndex: rowIndex,
    skuCode: resolvedSkuCode,
    skcCode: resolvedSkcCode,
  };

  if (headerType === '动态值') {
    if (!dynamicConfig && str) {
      // 仅当单元格已有内容时才允许按表头名辅助推断；空单元格保持空
      dynamicConfig = inferDynamicConfigForHeader(templateHeaderName, str);
    }
    if (!dynamicConfig) {
      if (!str || str === '动态值') cell.value = '';
      return;
    }
    let dynamicValue = await getDynamicValue(dynamicConfig, dataForDynamic, '');
    if (dynamicConfig.leftValue === '编号配对' || isImageDynamicConfig(dynamicConfig)) {
      await assignCellExportValue(cell, dynamicValue, dynamicConfig, assignCtx);
      return;
    }
    dynamicValue = await normalizeExportedImageValue(dynamicValue, dynamicConfig);
    if (dynamicValue) cell.value = dynamicValue;
    return;
  }

  if (headerType === '多个值' && str) {
    const lines = String(currentCellValue).split('\n');
    const processedLines = await Promise.all(
      lines.map(async (line) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return line;
        const lineConfig = parseDynamicPlaceholderLine(trimmedLine);
        if (lineConfig) {
          try {
            let dynamicValue = await getDynamicValue(lineConfig, dataForDynamic, '');
            if (isImageDynamicConfig(lineConfig)) {
              // 与动态值列一致：换公网链；失败则该行留空（不保留占位原文）
              const href = await resolveImageHrefForExportCell(dynamicValue, lineConfig);
              if (href) {
                if (shouldQueueImageDownload(dataForDynamic, lineConfig)) {
                  registerImageDownloadJob(imageDownloadJobs, {
                    url: href,
                    fileName: buildExportImageFileName(dataForDynamic, lineConfig),
                    source: '按模版导出',
                    meta: { stampName: item?.stampName, templateName, kind: lineConfig?.leftValue },
                  });
                }
                return href;
              }
              console.warn(`[按模板导出][多个值][${lineConfig.leftValue}] 行写入跳过`, {
                header: templateHeaderName,
                row: rowIndex,
                line: trimmedLine,
              });
              return '';
            }
            dynamicValue = await normalizeExportedImageValue(dynamicValue, lineConfig);
            return dynamicValue || line;
          } catch (error) {
            console.error('[导出] 多个值行解析失败:', error);
            throw error;
          }
        }
        return line;
      }),
    );
    const joined = processedLines.join('\n');
    cell.value = joined;
    writeValueToSameNamedExcelColumns(
      excelRow,
      templateHeaderName,
      excelHeaderMap?.__columnsByName || null,
      mapping?.excelColumn,
      joined,
    );
    return;
  }

  // 固定值/未配置标题列：不因有 AI 文案标题就自动填入（含多语言商品名称）
  // 若单元格仍是「结果图/主图/编号配对」占位符，走与动态值列相同的图片写入链路
  const inferred = parseDynamicPlaceholderLine(str);
  if (inferred) {
    const dynamicValue = await getDynamicValue(inferred, dataForDynamic, '');
    if (isImageDynamicConfig(inferred)) {
      await assignCellExportValue(cell, dynamicValue, inferred, assignCtx);
      return;
    }
    const textValue = await normalizeExportedImageValue(dynamicValue, inferred);
    if (textValue) cell.value = textValue;
    return;
  }

  // 货号类占位符替换；卖家SKU 已在上方排除
  if (str.includes('印花图名称') && headerType !== '固定值') {
    cell.value = String(currentCellValue).replace(/印花图名称/g, stampName);
  }
};

const arrayBufferToBase64 = (bytes) => {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  for (let i = 0; i < u8.length; i += 1) {
    binary += String.fromCharCode(u8[i]);
  }
  return btoa(binary);
};

/** 加载上架模版 Excel（API 多路径 + IndexedDB 缓存，与预览页逻辑一致） */
const resolveListingTemplateFileForExport = async (templateId, template) => {
  const { file } = await listingTemplateFileAPI.getByTemplate(templateId);
  if (file?.fileData) return file;

  const cached = await getFileFromIndexedDB(templateId);
  if (cached?.fileData?.length) {
    return {
      fileName: cached.fileName || 'template.xlsx',
      platform: cached.platform || template?.platform || '',
      fileData: arrayBufferToBase64(cached.fileData),
      tableData: cached.tableData || null,
      tableMetadata: {},
    };
  }

  return null;
};

/**
 * 按模版导出数据
 * @param {Array} exportData - 要导出的数据数组
 * @param {Array} templateIds - 选中的模版ID数组
 * @param {Function} onProgress - 进度回调函数
 * @param {Object} options - { runImageDownloads?: boolean, deferExcelDownloads?: boolean, onImageDownloadsStart?: (count) => void }
 * @returns {Promise<{ excelFiles: Array<{ fileName: string, buffer: ArrayBuffer }>, imageDownloadCount: number }>}
 */
export const exportToTemplate = async (exportData, templateIds, onProgress, options = {}) => {
  if (!exportData || exportData.length === 0) {
    throw new Error('没有要导出的数据');
  }

  if (!templateIds || templateIds.length === 0) {
    throw new Error('请至少选择一个上架模版');
  }

  exportTitleTranslateCache.clear();
  clearExportExternalUrlCache();

  const deferredExcelFiles = options.deferExcelDownloads ? [] : null;

  try {
    // 整次导出共享下载队列，避免多模版/多行重复触发同一图片下载
    const imageDownloadJobs = { list: [], seen: new Set() };

    // 遍历每个选中的模版
    for (let templateIndex = 0; templateIndex < templateIds.length; templateIndex++) {
      const templateId = templateIds[templateIndex];
      
      
      if (onProgress) {
        onProgress({
          current: templateIndex + 1,
          total: templateIds.length,
          templateId,
          status: 'loading',
        });
      }

      // 1. 获取模版信息
      const { template } = await listingTemplateAPI.getById(templateId);
      if (!template) {
        throw new Error(`模版 ${templateId} 不存在`);
      }
      const templateName = template.name || templateId;
      // 2. 获取模版文件（兼容 file-{id} 存储键与 IndexedDB 缓存）
      const templateFile = await resolveListingTemplateFileForExport(templateId, template);

      if (!templateFile?.fileData) {
        throw new Error(
          `模版「${template.name || templateId}」没有关联的表格文件，请在上架模版中重新上传 Excel 后再导出`,
        );
      }
      // 3. 解码base64文件数据
      const base64Data = templateFile.fileData;
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      // 4. 使用ExcelJS加载工作簿
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(bytes.buffer);
      // 5. 根据平台选择主数据工作表（SHEIN 需避开「有效属性值」等参考表）
      const platform = canonicalListingTemplatePlatform(template.platform || templateFile.platform);
      const worksheet = resolveExportWorksheet(workbook, platform);
      if (!worksheet) {
        throw new Error(`模版 ${template.name} 找不到对应的工作表`);
      }
      const sheetIndex = workbook.worksheets.indexOf(worksheet);
      // 6. 获取表头行索引和数据开始行索引
      const headerRowIndex =
        platform === 'SHEIN半托' || platform === 'SHEIN' ? 1 : getHeaderRowIndexByPlatform(platform);
      const dataStartRowIndex =
        getListingTemplateDataStartRowIndex(platform) ?? getDataStartRowIndexByPlatform(platform);

      // 7. 从tableMetadata中获取上架模版的表头
      const tableMetadata = templateFile.tableMetadata || {};
      let templateHeaders = [];
      let templateHeadersTable1 = [];
      let templateHeadersTable2 = [];
      
      // 根据不同平台获取表头
      if (platform === 'TEMU半托' || platform === 'TEMU') {
        // TEMU平台：同时获取table1和table2的表头
        templateHeadersTable1 = tableMetadata.table1?.headers || [];
        templateHeadersTable2 = tableMetadata.table2?.headers || [];
        // 默认使用table2（因为sheetIndex默认是2）
        templateHeaders = templateHeadersTable2;
      } else {
        // 其他平台直接使用headers
        templateHeaders = tableMetadata.headers || [];
      }

      // 如果templateHeaders是字符串数组，保持原样
      // 8. 读取Excel模版表头，建立列索引映射
      // TEMU平台需要同时处理table1和table2
      let table1Worksheet = null;
      let table2Worksheet = null;
      let excelHeaderMap = {}; // table2的Excel表头映射（默认）
      let excelHeaderMapTable1 = {}; // table1的Excel表头映射
      const excelHeaderColumnsByName = {}; // 同名表头 → 全部列号（含中/英重复轮播图）
      
      if (platform === 'TEMU半托' || platform === 'TEMU') {
        // TEMU平台：table1和table2在同一个工作表中（worksheet）
        // 都使用当前选中的工作表（sheetIndex=2，即table2的工作表）
        table1Worksheet = worksheet;
        table2Worksheet = worksheet;
        
        // 读取table1的Excel表头（使用同一个工作表）
        const table1HeaderRowIndex = getHeaderRowIndexByPlatform(platform); // TEMU第四行是表头（索引3）
        const table1ExcelHeaderRow = table1Worksheet.getRow(table1HeaderRowIndex + 1);
        table1ExcelHeaderRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
          const excelHeaderValue = cellValueToSearchString(cell.value).trim();
          if (excelHeaderValue) {
            excelHeaderMapTable1[excelHeaderValue] = colNumber;
            pushExcelHeaderColumn(excelHeaderColumnsByName, excelHeaderValue, colNumber);
          }
        });
        // 读取table2的Excel表头（使用同一个工作表）
        const table2HeaderRowIndex = getHeaderRowIndexByPlatform(platform); // TEMU第四行是表头（索引3）
        const table2ExcelHeaderRow = table2Worksheet.getRow(table2HeaderRowIndex + 1);
        table2ExcelHeaderRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
          const excelHeaderValue = cellValueToSearchString(cell.value).trim();
          if (excelHeaderValue) {
            excelHeaderMap[excelHeaderValue] = colNumber;
            pushExcelHeaderColumn(excelHeaderColumnsByName, excelHeaderValue, colNumber);
          }
        });
      } else {
        // 其他平台：只读取当前工作表的表头
        const excelHeaderRow = worksheet.getRow(headerRowIndex + 1);
        excelHeaderRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
          const excelHeaderValue = cellValueToSearchString(cell.value).trim();
          if (excelHeaderValue) {
            excelHeaderMap[excelHeaderValue] = colNumber;
            pushExcelHeaderColumn(excelHeaderColumnsByName, excelHeaderValue, colNumber);
          }
        });
      }
      // 挂到 map 上便于深层调用读取（不参与表头名匹配）
      excelHeaderMap.__columnsByName = excelHeaderColumnsByName;
      const carouselHeaderCols = Object.entries(excelHeaderColumnsByName)
        .filter(([name]) => /商品轮播图/.test(name))
        .map(([name, cols]) => ({ name, cols }));
      if (carouselHeaderCols.length > 0) {
        console.info('[导出] Excel 中商品轮播图列分布', carouselHeaderCols);
      }

      templateHeaders = ensureTemplateHeadersFromExcel(templateHeaders, excelHeaderMap);

      // 9. 建立上架模版表头到Excel表头的映射关系
      const headerMapping = {}; // table2的映射（默认）
      const headerMappingTable1 = {}; // table1的映射
      const headerDropdownValues = tableMetadata.headerDropdownValues || {};
      const dynamicValueConfigs = tableMetadata.dynamicValueConfigs || {};
      
      // 根据平台构造 refKey 前缀
      const getRefKeyPrefix = (platform, sheetIndex) => {
        if (platform === 'SHEIN半托' || platform === 'SHEIN') {
          return 'shein';
        } else if (platform === 'TEMU半托' || platform === 'TEMU') {
          return sheetIndex === 2 ? 'temu-table2' : 'temu-table1';
        } else if (platform === 'TikTok' || platform === 'TIKTOK') {
          return 'tiktok';
        } else {
          return 'dxm'; // 店小秘
        }
      };
      
      // TEMU平台：同时建立table1和table2的映射
      if (platform === 'TEMU半托' || platform === 'TEMU') {
        // 建立table1的映射
        const table1RefKeyPrefix = 'temu-table1';
        let table1TemplateDataColIndex = 0;
        const table1ColIndexMapForMapping = tableMetadata.table1?.colIndexMap || [];

        templateHeadersTable1.forEach((templateHeader, templateHeaderIndex) => {
          const templateHeaderName = typeof templateHeader === 'string' ? templateHeader : (templateHeader.name || templateHeader.displayName);
          
          // 跳过"+"列和"操作"列
          if (templateHeaderName === '+' || templateHeaderName === '操作' || templateHeaderName === '__ADD__' || templateHeaderName === '__OPERATION__') {
            return;
          }
          
          let excelColumnIndex = excelColumnFromTemuColIndexMap(
            table1ColIndexMapForMapping,
            table1TemplateDataColIndex,
          );
          if (!excelColumnIndex) {
            excelColumnIndex = excelHeaderMapTable1[templateHeaderName];
          }
          
          if (!excelColumnIndex) {
            if (typeof templateHeader === 'object' && templateHeader.displayName) {
              excelColumnIndex = excelHeaderMapTable1[templateHeader.displayName];
            }
            if (!excelColumnIndex) {
              const normalizedTemplateName = templateHeaderName.replace(/\s+/g, '').toLowerCase();
              for (const [excelHeaderName, colIndex] of Object.entries(excelHeaderMapTable1)) {
                const normalizedExcelName = excelHeaderName.replace(/\s+/g, '').toLowerCase();
                if (normalizedTemplateName === normalizedExcelName) {
                  excelColumnIndex = colIndex;
                  break;
                }
              }
            }
          }
          
          if (excelColumnIndex) {
            const refKey = `${table1RefKeyPrefix}-${templateHeaderIndex}`;
            const headerType = headerDropdownValues[refKey] || '固定值';
            // 计算adjustedColIndex：需要减去前面跳过的列数
            // 先计算当前索引前面有多少个跳过的列
            let skippedBefore = 0;
            for (let i = 0; i < templateHeaderIndex; i++) {
              const prevHeader = templateHeadersTable1[i];
              const prevHeaderName = typeof prevHeader === 'string' ? prevHeader : (prevHeader.name || prevHeader.displayName);
              if (prevHeaderName === '+' || prevHeaderName === '操作' || prevHeaderName === '__ADD__' || prevHeaderName === '__OPERATION__') {
                skippedBefore++;
              }
            }
            const adjustedColIndex = templateHeaderIndex - skippedBefore;
            
            let dynamicConfig = null;
            
            const colLevelKey = `${table1RefKeyPrefix}-${adjustedColIndex}`;
            
            if (dynamicValueConfigs[colLevelKey]) {
              dynamicConfig = dynamicValueConfigs[colLevelKey];
            } else {
              const rowLevelKey = `${table1RefKeyPrefix}-${adjustedColIndex}-0`;
              if (dynamicValueConfigs[rowLevelKey]) {
                dynamicConfig = dynamicValueConfigs[rowLevelKey];
              } else {
                for (let rowIndex = 0; rowIndex < 100; rowIndex++) {
                  const testKey = `${table1RefKeyPrefix}-${adjustedColIndex}-${rowIndex}`;
                  if (dynamicValueConfigs[testKey]) {
                    dynamicConfig = dynamicValueConfigs[testKey];
                    break;
                  }
                }
                if (!dynamicConfig) {
                }
              }
            }
            
            headerMappingTable1[templateHeaderIndex] = {
              excelColumn: excelColumnIndex,
              templateHeaderName,
              headerType,
              dynamicConfig,
              refKey,
              adjustedColIndex,
              templateDataColIndex: table1TemplateDataColIndex,
            };
          }
          // 无论是否映射成功，数据列序都推进（与 colIndexMap 下标对齐）
          table1TemplateDataColIndex++;
        });
        
      }
      
      // 建立table2的映射（或其他平台的映射）
      const refKeyPrefix = getRefKeyPrefix(platform, sheetIndex);
      // 计算templateDataColIndex（模版数据中的列索引，不包含"+"列和"操作"列）
      let templateDataColIndex = 0;
      const excelColsInOrder = [];
      if (platform === 'TEMU半托' || platform === 'TEMU') {
        const headerRowForCols = worksheet.getRow(getHeaderRowIndexByPlatform(platform) + 1);
        headerRowForCols.eachCell({ includeEmpty: false }, (_, colNumber) => {
          excelColsInOrder.push(colNumber);
        });
      }
      // TEMU：优先用解析时的 colIndexMap（避免同名「商品轮播图1」中英多列互相覆盖）
      const table2ColIndexMap = tableMetadata.table2?.colIndexMap || [];
      let templateColOrdinal = 0;

      templateHeaders.forEach((templateHeader, templateHeaderIndex) => {
        const templateHeaderName = typeof templateHeader === 'string' ? templateHeader : (templateHeader.name || templateHeader.displayName);
        
        // 跳过"+"列和"操作"列（这些列不在模版数据中）
        if (templateHeaderName === '+' || templateHeaderName === '操作' || templateHeaderName === '__ADD__' || templateHeaderName === '__OPERATION__') {
          return;
        }
        
        // 查找Excel中匹配的表头（支持多种匹配方式）
        let excelColumnIndex = null;
        if (platform === 'TEMU半托' || platform === 'TEMU') {
          excelColumnIndex = excelColumnFromTemuColIndexMap(table2ColIndexMap, templateColOrdinal);
        }
        if (!excelColumnIndex) {
          excelColumnIndex = excelHeaderMap[templateHeaderName];
        }
        if (
          !excelColumnIndex &&
          (platform === 'TEMU半托' || platform === 'TEMU') &&
          excelColsInOrder[templateColOrdinal] != null
        ) {
          excelColumnIndex = excelColsInOrder[templateColOrdinal];
        }
        templateColOrdinal += 1;
        
        // 如果直接匹配失败，尝试其他匹配方式
        if (!excelColumnIndex) {
          // 尝试匹配显示名称
          if (typeof templateHeader === 'object' && templateHeader.displayName) {
            excelColumnIndex = excelHeaderMap[templateHeader.displayName];
          }
          // 尝试模糊匹配（去除空格、特殊字符等）
          if (!excelColumnIndex) {
            const normalizedTemplateName = templateHeaderName.replace(/\s+/g, '').toLowerCase();
            for (const [excelHeaderName, colIndex] of Object.entries(excelHeaderMap)) {
              const normalizedExcelName = excelHeaderName.replace(/\s+/g, '').toLowerCase();
              if (normalizedTemplateName === normalizedExcelName) {
                excelColumnIndex = colIndex;
                break;
              }
            }
          }
        }
        
        if (excelColumnIndex) {
          // 构造 refKey（使用包含 "+" 列的原始 headerIndex）
          const refKey = `${refKeyPrefix}-${templateHeaderIndex}`;
          
          // headerType：仅认模板显式配置；未配置默认固定值（勿按「轮播图」表头名推断为动态值）
          const adjustedColIndex = templateHeaderIndex - 1;
          let dynamicConfig = null;

          const colLevelKey = `${refKeyPrefix}-${adjustedColIndex}`;
          if (dynamicValueConfigs[colLevelKey]) {
            dynamicConfig = dynamicValueConfigs[colLevelKey];
          } else {
            const rowLevelKey = `${refKeyPrefix}-${adjustedColIndex}-0`;
            if (dynamicValueConfigs[rowLevelKey]) {
              dynamicConfig = dynamicValueConfigs[rowLevelKey];
            } else {
              for (let rowIndex = 0; rowIndex < 100; rowIndex++) {
                const testKey = `${refKeyPrefix}-${adjustedColIndex}-${rowIndex}`;
                if (dynamicValueConfigs[testKey]) {
                  dynamicConfig = dynamicValueConfigs[testKey];
                  break;
                }
              }
            }
          }

          const headerType = resolveExportHeaderType(
            headerDropdownValues[refKey],
            Boolean(dynamicConfig),
          );

          headerMapping[templateHeaderIndex] = {
            excelColumn: excelColumnIndex,
            templateHeaderName,
            headerType,
            dynamicConfig,
            refKey,
            adjustedColIndex,
            templateDataColIndex, // 保存模版数据中的列索引
          };
          // 递增templateDataColIndex（只有非"+"列和"操作"列才递增）
          templateDataColIndex++;
        }
      });
      

      // 14. 开始处理导出预览数据

      // 10. 读取上架模版中的表格数据
      const tableData = templateFile.tableData || null;
      let templateTableRows = [];
      let templateTableRowsTable1 = [];
      let templateTableRowsTable2 = [];
      
      if (tableData) {
        if (platform === 'TEMU半托' || platform === 'TEMU') {
          // TEMU平台：同时获取table1和table2的数据
          templateTableRowsTable1 = tableData.table1Data || [];
          templateTableRowsTable2 = tableData.table2Data || [];
          // 默认使用table2（因为sheetIndex默认是2）
          templateTableRows = templateTableRowsTable2;
        } else {
          // 其他平台：直接使用tableData
          if (Array.isArray(tableData)) {
            templateTableRows = tableData;
          } else if (tableData.tableData) {
            templateTableRows = tableData.tableData;
          }
        }
      }

      // SHEIN：基本信息 / 商品属性是通用属性，须写入每一行商品信息行
      if (
        (platform === 'SHEIN半托' || platform === 'SHEIN')
        && Array.isArray(templateTableRows)
        && templateTableRows.length > 1
      ) {
        templateTableRows = propagateSheinSharedAttributeValues(
          templateHeaders,
          templateTableRows,
          tableMetadata.ruleSections || tableMetadata.sections
        );
      }
      // 11. 找到第一个完全空白的行（从dataStartRowIndex开始）
      let currentDataRow = dataStartRowIndex;
      const maxRows = worksheet.rowCount;
      
      for (let row = dataStartRowIndex + 1; row <= maxRows; row++) {
        const testRow = worksheet.getRow(row);
        let isEmpty = true;
        
        testRow.eachCell({ includeEmpty: false }, () => {
          isEmpty = false;
        });
        
        if (isEmpty) {
          currentDataRow = row - 1; // ExcelJS行号从1开始，所以减1
          break;
        }
      }

      // 如果没找到空白行，从最后一行之后开始
      if (currentDataRow < dataStartRowIndex) {
        currentDataRow = dataStartRowIndex;
      }
      if (platform === 'TEMU半托' || platform === 'TEMU') {
      }

      // 13. 填充上架模版表格数据到Excel（原样填充，包括占位符如"5000-印花图名称"）
      const filledRowIndices = []; // 记录填充的行索引，用于后续替换
      const filledRowIndicesTable1 = []; // TEMU平台：table1填充的行索引
      
      // TEMU平台：table1的当前数据行（提升到外层作用域，供后续项使用）
      let table1CurrentDataRow = null;
      
      // TEMU平台：先填充table1的数据
      if ((platform === 'TEMU半托' || platform === 'TEMU') && table1Worksheet && templateTableRowsTable1.length > 0) {
        // 找到table1的第一个空白行
        const table1DataStartRow = getDataStartRowIndexByPlatform(platform); // TEMU从第6行开始（索引5）
        table1CurrentDataRow = table1DataStartRow;
        const table1MaxRows = table1Worksheet.rowCount;
        
        for (let row = table1DataStartRow + 1; row <= table1MaxRows; row++) {
          const testRow = table1Worksheet.getRow(row);
          let isEmpty = true;
          testRow.eachCell({ includeEmpty: false }, () => {
            isEmpty = false;
          });
          if (isEmpty) {
            table1CurrentDataRow = row - 1;
            break;
          }
        }
        
        if (table1CurrentDataRow < table1DataStartRow) {
          table1CurrentDataRow = table1DataStartRow;
        }
        
        
        // 填充table1数据
        for (let rowIndex = 0; rowIndex < templateTableRowsTable1.length; rowIndex++) {
          const templateRow = templateTableRowsTable1[rowIndex];
          if (!Array.isArray(templateRow)) continue;
          
          const excelRow = table1Worksheet.getRow(table1CurrentDataRow + 1);
          
          // TEMU平台：在table1行的第一列强制填充"spu"
          if (platform === 'TEMU半托' || platform === 'TEMU') {
            const firstColumnCell = excelRow.getCell(1); // 第一列（列A）
            firstColumnCell.value = 'spu';
          }
          
          // 遍历上架模版的每一列
          templateRow.forEach((cellValue, templateColIndex) => {
            // 跳过"+"列和"操作"列
            if (cellValue === '__ADD__' || cellValue === '__OPERATION__') {
              return;
            }
            
            // 获取表头名称
            let templateHeaderName = '';
            if (templateColIndex < templateHeadersTable1.length) {
              const templateHeader = templateHeadersTable1[templateColIndex];
              templateHeaderName = typeof templateHeader === 'string' ? templateHeader : (templateHeader.name || templateHeader.displayName);
              if (templateHeaderName === '+' || templateHeaderName === '操作' || templateHeaderName === '__ADD__' || templateHeaderName === '__OPERATION__') {
                return;
              }
            }
            
            // 获取该列对应的Excel列索引
            const mapping = headerMappingTable1[templateColIndex];
            if (!mapping) {
              return;
            }
            
            if (!templateHeaderName) {
              templateHeaderName = mapping.templateHeaderName || '';
            }
            
            const excelColumn = mapping.excelColumn;
            const cell = excelRow.getCell(excelColumn);
            // TEMU：table1（组首行）商品层级固定 spu
            if ((platform === 'TEMU半托' || platform === 'TEMU') && isTemuProductLevelHeaderName(templateHeaderName)) {
              let hasDataInRow = false;
              excelRow.eachCell({ includeEmpty: false }, (checkCell) => {
                if (checkCell.value !== null && checkCell.value !== undefined && checkCell.value !== '') {
                  hasDataInRow = true;
                }
              });
              if (hasDataInRow || !cell.value || cell.value === '' || cell.value === null || cell.value === undefined) {
                cell.value = 'spu';
              }
              return;
            }
            
            // 原样填充数据（包括占位符），只有当单元格为空时才填充
            if (!cell.value || cell.value === '' || cell.value === null || cell.value === undefined) {
              let finalCellValue = '';
              
              // SPU 货号可拼印花名；卖家SKU/SKU货号不拼印花名
              if (mapping.headerType === 'SKU货号') {
                finalCellValue = String(cellValue || '');
              } else if (mapping.headerType === 'SPU货号') {
                finalCellValue = String(cellValue || '') + '印花图名称';
              } else {
                // 非SPU/SKU货号列，获取动态值配置（行级优先）
                let rowDynamicConfig = resolveDynamicConfigForMapping(
                  mapping,
                  rowIndex,
                  tableMetadata.dynamicValueConfigs,
                );
                
                
                if (mapping.headerType === '动态值' && rowDynamicConfig) {
                  const { leftValue, rightValue } = rowDynamicConfig;
                  if (leftValue === '印花图' || leftValue === '名称') {
                    finalCellValue = leftValue;
                  } else if (leftValue === '编号配对') {
                    finalCellValue = `编号配对-${rightValue || '第1张'}`;
                  } else if (leftValue && rightValue) {
                    if (leftValue === '标题' && rightValue !== '原标题') {
                      finalCellValue = `标题-${rightValue}`;
                    } else if (leftValue === '结果图' && rightValue) {
                      finalCellValue = `结果图-${rightValue}`;
                    } else {
                      finalCellValue = `${leftValue}-${rightValue}`;
                    }
                  } else {
                    finalCellValue = cellValue || '动态值';
                  }
                } else if (mapping.headerType === '多个值') {
                  finalCellValue = cellValue || '多个值';
                } else {
                  finalCellValue = cellValue || '';
                }
              }
              
              if (finalCellValue !== undefined && finalCellValue !== null && finalCellValue !== '') {
                cell.value = finalCellValue;
              }
            } else {
            }
          });
          
          filledRowIndicesTable1.push(table1CurrentDataRow);
          table1CurrentDataRow++;
        }
      }
      
      // 填充table2的数据（或其他平台的数据）
      if (templateTableRows.length > 0) {
        for (let rowIndex = 0; rowIndex < templateTableRows.length; rowIndex++) {
          const templateRow = templateTableRows[rowIndex];
          if (!Array.isArray(templateRow)) continue;
          
          const excelRow = worksheet.getRow(currentDataRow + 1);
          
          // TEMU：每一组首行 spu，其余行 sku（与官方模板一致）
          if (platform === 'TEMU半托' || platform === 'TEMU') {
            const firstColumnCell = excelRow.getCell(1);
            firstColumnCell.value = resolveTemuProductLevelByGroupRowIndex(rowIndex);
          }
          
          // 遍历上架模版的每一列
          templateRow.forEach((cellValue, templateColIndex) => {
            // 跳过"+"列和"操作"列（不导入到表格中）
            if (cellValue === '__ADD__' || cellValue === '__OPERATION__') {
              return;
            }
            
            // 获取表头名称
            let templateHeaderName = '';
            if (templateColIndex < templateHeaders.length) {
              const templateHeader = templateHeaders[templateColIndex];
              templateHeaderName = typeof templateHeader === 'string' ? templateHeader : (templateHeader.name || templateHeader.displayName);
              // 检查表头名称，如果是"+"列或"操作"列，也跳过
              if (templateHeaderName === '+' || templateHeaderName === '操作' || templateHeaderName === '__ADD__' || templateHeaderName === '__OPERATION__') {
                return;
              }
            }
            
            // 获取该列对应的Excel列索引
            const mapping = headerMapping[templateColIndex];
            if (!mapping) return; // 如果找不到映射，跳过（可能是"+"列或"操作"列）
            
            // 如果没有获取到表头名称，使用mapping中的名称
            if (!templateHeaderName) {
              templateHeaderName = mapping.templateHeaderName || '';
            }
            
            const excelColumn = mapping.excelColumn;
            const cell = excelRow.getCell(excelColumn);
            
            // TEMU：商品层级按组内行序写入（首行 spu，其余 sku）
            if ((platform === 'TEMU半托' || platform === 'TEMU') && isTemuProductLevelHeaderName(templateHeaderName)) {
              let hasDataInRow = false;
              excelRow.eachCell({ includeEmpty: false }, (checkCell) => {
                if (checkCell.value !== null && checkCell.value !== undefined && checkCell.value !== '') {
                  hasDataInRow = true;
                }
              });
              if (hasDataInRow || !cell.value || cell.value === '' || cell.value === null || cell.value === undefined) {
                cell.value = resolveTemuProductLevelByGroupRowIndex(rowIndex);
              }
              return;
            }
            
            // TEMU平台：如果table2的这一列是SPU货号列，且table1的SPU货号有数据，则从table1复制到所有table2行
            // table1和table2的第一行是对齐的，table1的SPU货号应该填充到所有table2行的SPU货号列
            if ((platform === 'TEMU半托' || platform === 'TEMU') && 
                (mapping.headerType === 'SPU货号' || templateHeaderName === 'SPU货号' || templateHeaderName.includes('SPU货号')) &&
                table1Worksheet && filledRowIndicesTable1.length > 0) {
              // 找到table1中与table2第一行对齐的行（table1的第一行索引）
              const table1RowIndex = filledRowIndicesTable1[0]; // table1只有一行，且与table2第一行对齐
              
              // 找到table1的SPU货号列
              let table1SPUValue = null;
              for (let table1ColIndex = 0; table1ColIndex < templateHeadersTable1.length; table1ColIndex++) {
                const table1Header = templateHeadersTable1[table1ColIndex];
                const table1HeaderName = typeof table1Header === 'string' ? table1Header : (table1Header.name || table1Header.displayName);
                
                if (table1HeaderName === 'SPU货号' || table1HeaderName.includes('SPU货号')) {
                  const table1Mapping = headerMappingTable1[table1ColIndex];
                  if (table1Mapping) {
                    const table1Row = table1Worksheet.getRow(table1RowIndex + 1);
                    const table1Cell = table1Row.getCell(table1Mapping.excelColumn);
                    const table1CellValue = table1Cell.value;
                    
                    // 如果table1的SPU货号有值（去除"印花图名称"占位符后仍有内容）
                    if (table1CellValue) {
                      const table1ValueStr = String(table1CellValue);
                      // 如果包含"印花图名称"，先去除它
                      if (table1ValueStr.includes('印花图名称')) {
                        table1SPUValue = table1ValueStr.replace(/印花图名称/g, '').trim();
                      } else {
                        table1SPUValue = table1ValueStr.trim();
                      }
                      
                      if (table1SPUValue) {
                        break;
                      }
                    }
                  }
                }
              }
              
              // 如果table1的SPU货号有值，强制填充到table2的这一列（覆盖已有值）
              if (table1SPUValue) {
                // 检查是否是SPU货号列（需要拼接"印花图名称"）
                if (mapping.headerType === 'SPU货号') {
                  cell.value = table1SPUValue + '印花图名称';
                } else {
                  cell.value = table1SPUValue;
                }
                return; // 跳过后续处理
              }
            }
            
            // 原样填充数据（包括占位符），只有当单元格为空时才填充
            if (!cell.value || cell.value === '' || cell.value === null || cell.value === undefined) {
              let finalCellValue = '';
              
              // 检查是否是SPU/SKU货号列（只检查headerType，不检查表头名称）
              if (mapping.headerType === 'SKU货号') {
                finalCellValue = String(cellValue || '');
              } else if (mapping.headerType === 'SPU货号') {
                finalCellValue = String(cellValue || '') + '印花图名称';
              } else {
                // 非SPU/SKU货号列，获取动态值配置（行级优先）
                let rowDynamicConfig = resolveDynamicConfigForMapping(
                  mapping,
                  rowIndex,
                  tableMetadata.dynamicValueConfigs,
                );
                
                
                // 根据headerType处理
                if (mapping.headerType === '动态值' && rowDynamicConfig) {
                  // 动态值列：填充占位符文本（如"标题"、"结果图"等）
                  const { leftValue, rightValue } = rowDynamicConfig;
                  if (leftValue === '印花图' || leftValue === '名称') {
                    finalCellValue = leftValue;
                  } else if (leftValue === '编号配对') {
                    finalCellValue = `编号配对-${rightValue || '第1张'}`;
                  } else if (leftValue && rightValue) {
                    // 构建占位符文本，例如："标题-英语"、"结果图-第1张"
                    if (leftValue === '标题' && rightValue !== '原标题') {
                      finalCellValue = `标题-${rightValue}`;
                    } else if (leftValue === '结果图' && rightValue) {
                      finalCellValue = `结果图-${rightValue}`;
                    } else {
                      finalCellValue = `${leftValue}-${rightValue}`;
                    }
                  } else {
                    // 如果没有配置，使用cellValue或默认占位符
                    finalCellValue = cellValue || '动态值';
                  }
                } else if (mapping.headerType === '多个值') {
                  // 多个值列：使用cellValue，如果为空则使用占位符
                  finalCellValue = cellValue || '多个值';
                } else {
                  // 其他固定值列
                  finalCellValue = cellValue || '';
                }
              }
              
              // 只有当finalCellValue不为空时才填充
              if (finalCellValue !== undefined && finalCellValue !== null && finalCellValue !== '') {
                cell.value = finalCellValue;
              }
            }
          });
          
          // TEMU：基本信息/商品属性随每一行商品信息写入同一 Excel 行
          if (
            (platform === 'TEMU半托' || platform === 'TEMU')
            && templateTableRowsTable1?.length > 0
            && headerMappingTable1
          ) {
            writeTemuTable1SharedColumnsToExcelRow({
              excelRow,
              templateRowTable1: templateTableRowsTable1[0],
              templateHeadersTable1,
              headerMappingTable1,
              dynamicValueConfigs: tableMetadata?.dynamicValueConfigs,
              skipProductLevel: true,
            });
          }

          filledRowIndices.push(currentDataRow);
          currentDataRow++;
        }
      } else {
      }

      // 无上架模版行配置时：从 Excel 数据区自动发现可替换行（兼容仅上传官方模版、未保存预览配置）
      if (filledRowIndices.length === 0 && exportData?.length > 0) {
        filledRowIndices.push(...discoverExportableRowIndices(worksheet, dataStartRowIndex));
      }

      // 14.1 循环处理exportData中的每个数据项：第一个项替换已有行，后续项添加新行
      // TEMU平台：需要同时检查table1和table2是否有填充的行
      const hasFilledRows = (platform === 'TEMU半托' || platform === 'TEMU') 
        ? (filledRowIndicesTable1.length > 0 || filledRowIndices.length > 0)
        : (filledRowIndices.length > 0);
      
      if (exportData && exportData.length > 0 && hasFilledRows) {
        // 保存原始填充的行索引（用于第一个项）
        const originalFilledRowIndices = [...filledRowIndices];
        const originalFilledRowIndicesTable1 = [...filledRowIndicesTable1]; // TEMU平台：table1的行索引
        
        // TEMU平台特殊处理：如果当前是table1，需要准备table2的相关数据，后续项从table2的下一行开始
        let temuTable2Worksheet = null;
        let temuTable2Headers = [];
        let temuTable2HeaderMapping = {};
        let temuTable2TableRows = [];
        let temuTable2LastRow = null;
        let temuTable2CurrentDataRow = null;
        
        if (platform === 'TEMU半托' || platform === 'TEMU') {
          // TEMU平台：table1和table2在同一个工作表中，都使用worksheet
          temuTable2Worksheet = worksheet;
          
          // 获取table2的表头
          temuTable2Headers = tableMetadata.table2?.headers || [];
          
          // 读取table2的Excel表头，建立列索引映射（使用同一个工作表）
          const table2HeaderRowIndex = getHeaderRowIndexByPlatform(platform); // TEMU第四行是表头（索引3）
          const table2ExcelHeaderRow = temuTable2Worksheet.getRow(table2HeaderRowIndex + 1);
          const table2ExcelHeaderMap = {};
          
          table2ExcelHeaderRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
            const excelHeaderValue = cell.value?.toString().trim() || '';
            if (excelHeaderValue) {
              table2ExcelHeaderMap[excelHeaderValue] = colNumber;
            }
          });
          
          if (temuTable2Worksheet) {
              
              // 构建table2的表头映射
              const table2RefKeyPrefix = 'temu-table2';
              let table2TemplateDataColIndex = 0;
              
              const table2ExcelColsInOrder = [];
              table2ExcelHeaderRow.eachCell({ includeEmpty: false }, (_, colNumber) => {
                table2ExcelColsInOrder.push(colNumber);
              });
              let table2TemplateColOrdinal = 0;

              temuTable2Headers.forEach((templateHeader, templateHeaderIndex) => {
                const templateHeaderName = typeof templateHeader === 'string' ? templateHeader : (templateHeader.name || templateHeader.displayName);
                
                if (templateHeaderName === '+' || templateHeaderName === '操作' || templateHeaderName === '__ADD__' || templateHeaderName === '__OPERATION__') {
                  return;
                }
                
                let excelColumnIndex = table2ExcelHeaderMap[templateHeaderName];
                if (!excelColumnIndex && table2ExcelColsInOrder[table2TemplateColOrdinal] != null) {
                  excelColumnIndex = table2ExcelColsInOrder[table2TemplateColOrdinal];
                }
                table2TemplateColOrdinal += 1;
                
                if (!excelColumnIndex) {
                  if (typeof templateHeader === 'object' && templateHeader.displayName) {
                    excelColumnIndex = table2ExcelHeaderMap[templateHeader.displayName];
                  }
                  if (!excelColumnIndex) {
                    const normalizedTemplateName = templateHeaderName.replace(/\s+/g, '').toLowerCase();
                    for (const [excelHeaderName, colIndex] of Object.entries(table2ExcelHeaderMap)) {
                      const normalizedExcelName = excelHeaderName.replace(/\s+/g, '').toLowerCase();
                      if (normalizedTemplateName === normalizedExcelName) {
                        excelColumnIndex = colIndex;
                        break;
                      }
                    }
                  }
                }
                
                if (excelColumnIndex) {
                  const refKey = `${table2RefKeyPrefix}-${templateHeaderIndex}`;
                  const headerType = headerDropdownValues[refKey] || '固定值';
                  const adjustedColIndex = templateHeaderIndex - 1;
                  
                  let dynamicConfig = null;
                  const colLevelKey = `${table2RefKeyPrefix}-${adjustedColIndex}`;
                  if (dynamicValueConfigs[colLevelKey]) {
                    dynamicConfig = dynamicValueConfigs[colLevelKey];
                  } else {
                    const rowLevelKey = `${table2RefKeyPrefix}-${adjustedColIndex}-0`;
                    if (dynamicValueConfigs[rowLevelKey]) {
                      dynamicConfig = dynamicValueConfigs[rowLevelKey];
                    }
                  }
                  
                  temuTable2HeaderMapping[templateHeaderIndex] = {
                    excelColumn: excelColumnIndex,
                    templateHeaderName,
                    headerType,
                    dynamicConfig,
                    refKey,
                    adjustedColIndex,
                    templateDataColIndex: table2TemplateDataColIndex,
                  };
                  
                  table2TemplateDataColIndex++;
                }
              });
              
              // 获取table2的模版数据
              if (tableData) {
                temuTable2TableRows = tableData.table2Data || [];
              }
              
            // 找到table2的最后一行有数据的行
            const table2DataStartRow = getDataStartRowIndexByPlatform(platform); // TEMU从第6行开始（索引5）
            let lastRowWithData = table2DataStartRow;
            
            for (let row = table2DataStartRow + 1; row <= temuTable2Worksheet.rowCount; row++) {
              const testRow = temuTable2Worksheet.getRow(row);
              let hasData = false;
              testRow.eachCell({ includeEmpty: false }, () => {
                hasData = true;
              });
              if (hasData) {
                lastRowWithData = row - 1;
              } else {
                break;
              }
            }
            
            temuTable2LastRow = lastRowWithData;
            temuTable2CurrentDataRow = temuTable2LastRow + 1;
          }
        }
        
        const exportRowsPlan = [];

        // 遍历每个exportData项，逐个处理
        for (let itemIndex = 0; itemIndex < exportData.length; itemIndex++) {
          const item = exportData[itemIndex];
          const stampName = item.stampName || '';
          // 如果是第一个项，使用已有的行；如果是后续项，需要复制模版数据到新行
          let targetRowIndices = [];
          
          if (itemIndex === 0) {
            // 第一个项：使用已有的行
            targetRowIndices = originalFilledRowIndices;
            if (platform === 'TEMU半托' || platform === 'TEMU') {
              if (originalFilledRowIndicesTable1.length > 0) {
              }
            }
          } else {
            // 后续项：复制模版数据到新行
            // TEMU平台特殊处理：如果当前是table1，后续项应该从table2的下一行开始
            let targetWorksheet = worksheet;
            let targetHeaders = templateHeaders;
            let targetHeaderMapping = headerMapping;
            let targetTableRows = templateTableRows;
            let targetCurrentDataRow = currentDataRow;
            
            if ((platform === 'TEMU半托' || platform === 'TEMU') && temuTable2Worksheet) {
              // TEMU平台：后续项应该添加到table2（使用同一个工作表）
              targetWorksheet = temuTable2Worksheet;
              targetHeaders = temuTable2Headers;
              targetHeaderMapping = temuTable2HeaderMapping;
              targetTableRows = temuTable2TableRows;
              targetCurrentDataRow = temuTable2CurrentDataRow;
            }
            
            // TEMU平台：后续项需要同时复制table1和table2，table1应该和table2的第一行对齐
            if ((platform === 'TEMU半托' || platform === 'TEMU') && templateTableRowsTable1.length > 0 && table1Worksheet) {
              // 先确定table2的第一行位置，然后将table1填充到相同位置
              const table1TargetRowIndex = targetCurrentDataRow; // table1应该和table2的第一行对齐

              // 遍历table1的模版数据
              let currentTable1Row = table1TargetRowIndex;
              for (let rowIndex = 0; rowIndex < templateTableRowsTable1.length; rowIndex++) {
                const templateRow = templateTableRowsTable1[rowIndex];
                if (!Array.isArray(templateRow)) continue;
                
                const excelRow = table1Worksheet.getRow(currentTable1Row + 1);
                
                // TEMU平台：在table1行的第一列强制填充"spu"
                if (platform === 'TEMU半托' || platform === 'TEMU') {
                  const firstColumnCell = excelRow.getCell(1); // 第一列（列A）
                  firstColumnCell.value = 'spu';
                }
                
                // 遍历上架模版的每一列
                templateRow.forEach((cellValue, templateColIndex) => {
                  // 跳过"+"列和"操作"列
                  if (cellValue === '__ADD__' || cellValue === '__OPERATION__') {
                    return;
                  }
                  
                  // 获取表头名称
                  let templateHeaderName = '';
                  if (templateColIndex < templateHeadersTable1.length) {
                    const templateHeader = templateHeadersTable1[templateColIndex];
                    templateHeaderName = typeof templateHeader === 'string' ? templateHeader : (templateHeader.name || templateHeader.displayName);
                    if (templateHeaderName === '+' || templateHeaderName === '操作' || templateHeaderName === '__ADD__' || templateHeaderName === '__OPERATION__') {
                      return;
                    }
                  }
                  
                  // 获取该列对应的Excel列索引
                  const mapping = headerMappingTable1[templateColIndex];
                  if (!mapping) return;
                  
                  if (!templateHeaderName) {
                    templateHeaderName = mapping.templateHeaderName || '';
                  }
                  
                  const excelColumn = mapping.excelColumn;
                  const cell = excelRow.getCell(excelColumn);
                  
                  if (isTemuProductLevelHeaderName(templateHeaderName)) {
                    cell.value = 'spu';
                    return;
                  }
                  
                  // 填充数据（包括占位符）
                  let finalCellValue = '';
                  
                  // 检查是否是SPU/SKU货号列（只检查headerType，不检查表头名称）
                  if (mapping.headerType === 'SKU货号') {
                    finalCellValue = String(cellValue || '');
                  } else if (mapping.headerType === 'SPU货号') {
                    finalCellValue = String(cellValue || '') + '印花图名称';
                  } else {
                    // 非SPU/SKU货号列，获取动态值配置（行级优先）
                    let rowDynamicConfig = resolveDynamicConfigForMapping(
                      mapping,
                      rowIndex,
                      tableMetadata.dynamicValueConfigs,
                    );
                    
                    if (mapping.headerType === '动态值' && rowDynamicConfig) {
                      const { leftValue, rightValue } = rowDynamicConfig;
                      if (leftValue === '印花图' || leftValue === '名称') {
                        finalCellValue = leftValue;
                      } else if (leftValue === '编号配对') {
                        finalCellValue = `编号配对-${rightValue || '第1张'}`;
                      } else if (leftValue && rightValue) {
                        if (leftValue === '标题' && rightValue !== '原标题') {
                          finalCellValue = `标题-${rightValue}`;
                        } else if (leftValue === '结果图' && rightValue) {
                          finalCellValue = `结果图-${rightValue}`;
                        } else {
                          finalCellValue = `${leftValue}-${rightValue}`;
                        }
                      } else {
                        finalCellValue = cellValue || '动态值';
                      }
                    } else if (mapping.headerType === '多个值') {
                      finalCellValue = cellValue || '多个值';
                    } else {
                      finalCellValue = cellValue || '';
                    }
                  }
                  
                  if (finalCellValue !== undefined && finalCellValue !== null && finalCellValue !== '') {
                    cell.value = finalCellValue;
                  }
                });
                
                currentTable1Row++;
              }
              
              // 更新table1CurrentDataRow为table1的最后一行+1，用于下次填充
              table1CurrentDataRow = currentTable1Row;
            }
            
            // 从原始模版数据复制到新行
            if (targetTableRows.length > 0 || itemIndex > 0) {
              // 如果模板没有预配置数据行，使用表头创建一个空行结构
              const rowsToProcess = targetTableRows.length > 0
                  ? targetTableRows
                  : [targetHeaders.map(h => {
                    const name = typeof h === 'string' ? h : (h.name || h.displayName || '');
                    if (name === '+' || name === '操作' || name === '__ADD__' || name === '__OPERATION__') {
                      return name;
                    }
                    return '';
                  })];

              for (let rowIndex = 0; rowIndex < rowsToProcess.length; rowIndex++) {
                const templateRow = rowsToProcess[rowIndex];
                if (!Array.isArray(templateRow)) continue;

                const rowTargetCurrentDataRow = targetCurrentDataRow;
                const rowTable1CurrentDataRow = table1CurrentDataRow;

                const excelRow = targetWorksheet.getRow(rowTargetCurrentDataRow + 1);

                // TEMU：每一组首行 spu，其余行 sku
                if (platform === 'TEMU半托' || platform === 'TEMU') {
                  const firstColumnCell = excelRow.getCell(1);
                  firstColumnCell.value = resolveTemuProductLevelByGroupRowIndex(rowIndex);
                }

                // 遍历上架模版的每一列
                templateRow.forEach((cellValue, templateColIndex) => {
                  // 跳过"+"列和"操作"列
                  if (cellValue === '__ADD__' || cellValue === '__OPERATION__') {
                    return;
                  }
                  
                  // 获取表头名称
                  let templateHeaderName = '';
                  if (templateColIndex < targetHeaders.length) {
                    const templateHeader = targetHeaders[templateColIndex];
                    templateHeaderName = typeof templateHeader === 'string' ? templateHeader : (templateHeader.name || templateHeader.displayName);
                    if (templateHeaderName === '+' || templateHeaderName === '操作' || templateHeaderName === '__ADD__' || templateHeaderName === '__OPERATION__') {
                      return;
                    }
                  }
                  
                  // 获取该列对应的Excel列索引
                  const mapping = targetHeaderMapping[templateColIndex];
                  if (!mapping) return;
                  
                  if (!templateHeaderName) {
                    templateHeaderName = mapping.templateHeaderName || '';
                  }
                  
                  const excelColumn = mapping.excelColumn;
                  const cell = excelRow.getCell(excelColumn);
                  
                  // TEMU：后续项商品层级按组内行序（首行 spu，其余 sku）
                  if ((platform === 'TEMU半托' || platform === 'TEMU') && isTemuProductLevelHeaderName(templateHeaderName)) {
                    let hasDataInRow = false;
                    excelRow.eachCell({ includeEmpty: false }, (checkCell) => {
                      if (checkCell.value !== null && checkCell.value !== undefined && checkCell.value !== '') {
                        hasDataInRow = true;
                      }
                    });
                    if (hasDataInRow || !cell.value || cell.value === '' || cell.value === null || cell.value === undefined) {
                      cell.value = resolveTemuProductLevelByGroupRowIndex(rowIndex);
                    }
                    return;
                  }
                  
                  // TEMU平台：如果table2的这一列是SPU货号列，且table1的SPU货号有数据，则从table1复制到所有table2行
                  // 后续项复制时，table1和table2的第一行是对齐的（都是targetCurrentDataRow）
                  if ((platform === 'TEMU半托' || platform === 'TEMU') && 
                      (mapping.headerType === 'SPU货号' || templateHeaderName === 'SPU货号' || templateHeaderName.includes('SPU货号')) &&
                      table1Worksheet && rowTable1CurrentDataRow !== null) {
                    // 找到table1的SPU货号列
                    // table1的行应该是table1TargetRowIndex（与table2第一行对齐）
                    const table1TargetRowIndex = rowTargetCurrentDataRow; // table1和table2的第一行对齐
                    let table1SPUValue = null;
                    
                    for (let table1ColIndex = 0; table1ColIndex < templateHeadersTable1.length; table1ColIndex++) {
                      const table1Header = templateHeadersTable1[table1ColIndex];
                      const table1HeaderName = typeof table1Header === 'string' ? table1Header : (table1Header.name || table1Header.displayName);
                      
                      if (table1HeaderName === 'SPU货号' || table1HeaderName.includes('SPU货号')) {
                        const table1Mapping = headerMappingTable1[table1ColIndex];
                        if (table1Mapping) {
                          const table1Row = table1Worksheet.getRow(table1TargetRowIndex + 1);
                          const table1Cell = table1Row.getCell(table1Mapping.excelColumn);
                          const table1CellValue = table1Cell.value;
                          
                          if (table1CellValue) {
                            const table1ValueStr = String(table1CellValue);
                            if (table1ValueStr.includes('印花图名称')) {
                              table1SPUValue = table1ValueStr.replace(/印花图名称/g, '').trim();
                            } else {
                              table1SPUValue = table1ValueStr.trim();
                            }
                            
                            if (table1SPUValue) {
                              break;
                            }
                          }
                        }
                      }
                    }
                    
                    // 如果table1的SPU货号有值，强制填充到table2的这一列（覆盖已有值）
                    if (table1SPUValue) {
                      if (mapping.headerType === 'SPU货号') {
                        cell.value = table1SPUValue + '印花图名称';
                      } else {
                        cell.value = table1SPUValue;
                      }
                      return; // 跳过后续处理
                    }
                  }
                  
                  // 填充数据（包括占位符）
                  let finalCellValue = '';
                  
                  // 检查是否是SPU/SKU货号列（只检查headerType，不检查表头名称）
                  if (mapping.headerType === 'SKU货号') {
                    finalCellValue = String(cellValue || '');
                  } else if (mapping.headerType === 'SPU货号') {
                    finalCellValue = String(cellValue || '') + '印花图名称';
                  } else {
                    // 非SPU/SKU货号列，获取动态值配置（行级优先）
                    let rowDynamicConfig = resolveDynamicConfigForMapping(
                      mapping,
                      rowIndex,
                      dynamicValueConfigs,
                    );
                    
                    if (mapping.headerType === '动态值' && rowDynamicConfig) {
                      const { leftValue, rightValue } = rowDynamicConfig;
                      if (leftValue === '印花图' || leftValue === '名称') {
                        finalCellValue = leftValue;
                      } else if (leftValue === '编号配对') {
                        finalCellValue = `编号配对-${rightValue || '第1张'}`;
                      } else if (leftValue && rightValue) {
                        if (leftValue === '标题' && rightValue !== '原标题') {
                          finalCellValue = `标题-${rightValue}`;
                        } else if (leftValue === '结果图' && rightValue) {
                          finalCellValue = `结果图-${rightValue}`;
                        } else {
                          finalCellValue = `${leftValue}-${rightValue}`;
                        }
                      } else {
                        finalCellValue = cellValue || '动态值';
                      }
                    } else if (mapping.headerType === '多个值') {
                      finalCellValue = cellValue || '多个值';
                    } else {
                      finalCellValue = cellValue || '';
                    }
                  }
                  
                  if (finalCellValue !== undefined && finalCellValue !== null && finalCellValue !== '') {
                    cell.value = finalCellValue;
                  }
                });

                // TEMU：后续项每一行商品信息同步写入基本信息/商品属性
                if (
                  (platform === 'TEMU半托' || platform === 'TEMU')
                  && templateTableRowsTable1?.length > 0
                  && headerMappingTable1
                ) {
                  writeTemuTable1SharedColumnsToExcelRow({
                    excelRow,
                    templateRowTable1: templateTableRowsTable1[0],
                    templateHeadersTable1,
                    headerMappingTable1,
                    dynamicValueConfigs: tableMetadata?.dynamicValueConfigs,
                    skipProductLevel: true,
                  });
                }
                
                targetRowIndices.push(targetCurrentDataRow);
                targetCurrentDataRow++;
              }
              
              // 更新currentDataRow或temuTable2CurrentDataRow
              if ((platform === 'TEMU半托' || platform === 'TEMU') && temuTable2Worksheet) {
                temuTable2CurrentDataRow = targetCurrentDataRow;
              } else {
                currentDataRow = targetCurrentDataRow;
              }
            }
          }
          
          // TEMU平台第一个项：需要同时替换table1和table2
          if ((platform === 'TEMU半托' || platform === 'TEMU') && itemIndex === 0 && originalFilledRowIndicesTable1.length > 0) {
            // 先替换table1的行
            for (let rowIndex = 0; rowIndex < originalFilledRowIndicesTable1.length; rowIndex++) {
              const targetRowIndex = originalFilledRowIndicesTable1[rowIndex];
              const excelRow = table1Worksheet.getRow(targetRowIndex + 1);
              
              // 遍历table1的所有表头，替换对应的占位符
              for (let templateHeaderIndex = 0; templateHeaderIndex < templateHeadersTable1.length; templateHeaderIndex++) {
                const templateHeader = templateHeadersTable1[templateHeaderIndex];
                const templateHeaderName = typeof templateHeader === 'string' ? templateHeader : (templateHeader.name || templateHeader.displayName);
                
                if (templateHeaderName === '+' || templateHeaderName === '操作' || templateHeaderName === '__ADD__' || templateHeaderName === '__OPERATION__') {
                  continue;
                }
                
                const mapping = headerMappingTable1[templateHeaderIndex];
                if (!mapping) {
                  continue;
                }
                
                const { excelColumn } = mapping;
                const cell = excelRow.getCell(excelColumn);
                const currentCellValue = cell.value || '';
                
                
                if ((platform === 'TEMU半托' || platform === 'TEMU') && isTemuProductLevelHeaderName(templateHeaderName)) {
                  let hasDataInRow = false;
                  excelRow.eachCell({ includeEmpty: false }, (checkCell) => {
                    if (checkCell.value !== null && checkCell.value !== undefined && checkCell.value !== '') {
                      hasDataInRow = true;
                    }
                  });
                  if (hasDataInRow || !cell.value || cell.value === '' || cell.value === null || cell.value === undefined) {
                    cell.value = 'spu';
                  }
                  continue;
                }
                
                const { should: needsDynamicResolveRow, inferredConfig: inferredRowConfig } =
                  shouldResolveExportCell(mapping, templateHeaderName, currentCellValue, item);

                if (needsDynamicResolveRow) {
                  await applyDynamicValueToExportCell({
                    cell,
                    mapping,
                    item,
                    platform,
                    templateHeaderName,
                    stampName,
                    dynamicValueConfigs,
                    rowIndex,
                    imageDownloadJobs,
                    templateName,
                    inferredConfig: inferredRowConfig,
                    templateRow: templateTableRowsTable1?.[rowIndex],
                    templateHeaders: templateHeadersTable1,
                    headerMapping: headerMappingTable1,
                    excelRow,
                    excelHeaderMap,
                  });
                }
              }
            }
          }
          
          // TEMU平台后续项：需要同时替换table1和table2
          if ((platform === 'TEMU半托' || platform === 'TEMU') && itemIndex > 0 && templateTableRowsTable1.length > 0 && table1Worksheet) {
            // 替换table1的行（table1应该和table2的第一行对齐）
            // 找到table2的第一行索引（targetRowIndices的第一个）
            const table2FirstRowIndex = targetRowIndices.length > 0 ? targetRowIndices[0] : null;
            const table1TargetRowIndex = table2FirstRowIndex !== null ? table2FirstRowIndex : (table1CurrentDataRow !== null ? table1CurrentDataRow - templateTableRowsTable1.length : null);
            
            if (table1TargetRowIndex !== null) {
              for (let rowIndex = 0; rowIndex < templateTableRowsTable1.length; rowIndex++) {
                const targetRowIndex = table1TargetRowIndex + rowIndex;
                const excelRow = table1Worksheet.getRow(targetRowIndex + 1);
                
                // 遍历table1的所有表头，替换对应的占位符
                for (let templateHeaderIndex = 0; templateHeaderIndex < templateHeadersTable1.length; templateHeaderIndex++) {
                  const templateHeader = templateHeadersTable1[templateHeaderIndex];
                  const templateHeaderName = typeof templateHeader === 'string' ? templateHeader : (templateHeader.name || templateHeader.displayName);
                  
                  if (templateHeaderName === '+' || templateHeaderName === '操作' || templateHeaderName === '__ADD__' || templateHeaderName === '__OPERATION__') {
                    continue;
                  }
                  
                  const mapping = headerMappingTable1[templateHeaderIndex];
                  if (!mapping) continue;
                  
                  const { excelColumn } = mapping;
                  const cell = excelRow.getCell(excelColumn);
                  const currentCellValue = cell.value || '';
                  
                if (isTemuProductLevelHeaderName(templateHeaderName)) {
                  let hasDataInRow = false;
                  excelRow.eachCell({ includeEmpty: false }, (checkCell) => {
                    if (checkCell.value !== null && checkCell.value !== undefined && checkCell.value !== '') {
                      hasDataInRow = true;
                    }
                  });
                  if (hasDataInRow || !cell.value || cell.value === '' || cell.value === null || cell.value === undefined) {
                    cell.value = 'spu';
                  }
                  continue;
                }
                  
                  const { should: needsDynamicResolveRow, inferredConfig: inferredRowConfig } =
                    shouldResolveExportCell(mapping, templateHeaderName, currentCellValue, item);

                  if (needsDynamicResolveRow) {
                    await applyDynamicValueToExportCell({
                      cell,
                      mapping,
                      item,
                      platform,
                      templateHeaderName,
                      stampName,
                      dynamicValueConfigs,
                      rowIndex,
                      imageDownloadJobs,
                      templateName,
                      inferredConfig: inferredRowConfig,
                      templateRow: templateTableRowsTable1?.[rowIndex],
                      templateHeaders: templateHeadersTable1,
                      headerMapping: headerMappingTable1,
                      excelRow,
                      excelHeaderMap,
                    });
                  }
                }
              }
            }
          }
          
          // 遍历所有目标行，替换占位符
          // 确定使用哪个工作表、表头和映射
          let replaceWorksheet = worksheet;
          let replaceHeaders = templateHeaders;
          let replaceHeaderMapping = headerMapping;
          
          if ((platform === 'TEMU半托' || platform === 'TEMU') && itemIndex > 0 && temuTable2Worksheet) {
            // 后续项使用table2（使用同一个工作表）
            replaceWorksheet = temuTable2Worksheet;
            replaceHeaders = temuTable2Headers;
            replaceHeaderMapping = temuTable2HeaderMapping;
          }
          
          for (let rowIndex = 0; rowIndex < targetRowIndices.length; rowIndex++) {
            const targetRowIndex = targetRowIndices[rowIndex];
            const excelRow = replaceWorksheet.getRow(targetRowIndex + 1);
            
            // 遍历所有表头，替换对应的占位符
            for (let templateHeaderIndex = 0; templateHeaderIndex < replaceHeaders.length; templateHeaderIndex++) {
              const templateHeader = replaceHeaders[templateHeaderIndex];
              const templateHeaderName = typeof templateHeader === 'string' ? templateHeader : (templateHeader.name || templateHeader.displayName);
              
              // 跳过"+"列和"操作"列
              if (templateHeaderName === '+' || templateHeaderName === '操作' || templateHeaderName === '__ADD__' || templateHeaderName === '__OPERATION__') {
                continue;
              }
              
              // 获取该列的配置
              const mapping = replaceHeaderMapping[templateHeaderIndex];
              if (!mapping) continue;
              
              const { headerType, excelColumn } = mapping;
              const cell = excelRow.getCell(excelColumn);
              const currentCellValue = cell.value || '';
              
              
              // TEMU：占位符替换阶段仍按组内行序写商品层级（首行 spu，其余 sku）
              if ((platform === 'TEMU半托' || platform === 'TEMU') && isTemuProductLevelHeaderName(templateHeaderName)) {
                let hasDataInRow = false;
                excelRow.eachCell({ includeEmpty: false }, (checkCell) => {
                  if (checkCell.value !== null && checkCell.value !== undefined && checkCell.value !== '') {
                    hasDataInRow = true;
                  }
                });
                if (hasDataInRow || !cell.value || cell.value === '' || cell.value === null || cell.value === undefined) {
                  cell.value = resolveTemuProductLevelByGroupRowIndex(rowIndex);
                }
                continue;
              }
              
              // TEMU平台：如果table2的这一列是SPU货号列，且table1的SPU货号有数据，则从table1复制
              if ((platform === 'TEMU半托' || platform === 'TEMU') && 
                  (headerType === 'SPU货号' || templateHeaderName === 'SPU货号' || templateHeaderName.includes('SPU货号')) &&
                  table1Worksheet && originalFilledRowIndicesTable1.length > 0) {
                // 找到table1的SPU货号列（table1的第一行）
                const table1RowIndex = originalFilledRowIndicesTable1[0];
                let table1SPUValue = null;
                
                for (let table1ColIndex = 0; table1ColIndex < templateHeadersTable1.length; table1ColIndex++) {
                  const table1Header = templateHeadersTable1[table1ColIndex];
                  const table1HeaderName = typeof table1Header === 'string' ? table1Header : (table1Header.name || table1Header.displayName);
                  
                  if (table1HeaderName === 'SPU货号' || table1HeaderName.includes('SPU货号')) {
                    const table1Mapping = headerMappingTable1[table1ColIndex];
                    if (table1Mapping) {
                      const table1Row = table1Worksheet.getRow(table1RowIndex + 1);
                      const table1Cell = table1Row.getCell(table1Mapping.excelColumn);
                      const table1CellValue = table1Cell.value;
                      
                      if (table1CellValue) {
                        const table1ValueStr = String(table1CellValue);
                        if (table1ValueStr.includes('印花图名称')) {
                          table1SPUValue = table1ValueStr.replace(/印花图名称/g, '').trim();
                        } else {
                          table1SPUValue = table1ValueStr.trim();
                        }
                        
                        if (table1SPUValue) {
                          break;
                        }
                      }
                    }
                  }
                }
                
                // 如果table1的SPU货号有值，强制填充到table2的这一列（覆盖已有值）
                if (table1SPUValue) {
                  if (headerType === 'SPU货号') {
                    cell.value = table1SPUValue + '印花图名称';
                  } else {
                    cell.value = table1SPUValue;
                  }
                  continue; // 跳过后续处理
                }
              }
              
              const { should: needsDynamicResolve, inferredConfig: inferredRowConfig } =
                shouldResolveExportCell(mapping, templateHeaderName, currentCellValue, item);

              if (needsDynamicResolve) {
                await applyDynamicValueToExportCell({
                  cell,
                  mapping,
                  item,
                  platform,
                  templateHeaderName,
                  stampName,
                  dynamicValueConfigs,
                  rowIndex,
                  imageDownloadJobs,
                  templateName,
                  inferredConfig: inferredRowConfig,
                  templateRow: templateTableRows?.[rowIndex],
                  templateHeaders: replaceHeaders,
                  headerMapping: replaceHeaderMapping,
                  excelRow,
                  excelHeaderMap,
                });
              }
            }

            // 兜底填表：列映射已登记过下载任务，此处不再重复登记
            await replacePlaceholdersInExcelRow({
              excelRow,
              excelHeaderMap,
              item,
              platform,
              stampName,
              dynamicValueConfigs,
              imageDownloadJobs,
              templateName,
              rowIndex,
              templateRow: templateTableRows?.[rowIndex] || null,
              templateHeaders: replaceHeaders,
              headerMapping: replaceHeaderMapping,
            });
          }

          if (targetRowIndices.length > 0) {
            const maxUsed = Math.max(...targetRowIndices);
            if ((platform === 'TEMU半托' || platform === 'TEMU') && temuTable2Worksheet) {
              temuTable2CurrentDataRow = Math.max(temuTable2CurrentDataRow ?? 0, maxUsed + 1);
            } else {
              currentDataRow = Math.max(currentDataRow, maxUsed + 1);
            }
            exportRowsPlan.push({
              item,
              rowIndices: [...targetRowIndices],
              templateRow: templateTableRows?.[0] || null,
              templateHeaders: replaceHeaders,
              headerMapping: replaceHeaderMapping,
            });
          }
        }

        for (const plan of exportRowsPlan) {
          for (let groupRowIndex = 0; groupRowIndex < plan.rowIndices.length; groupRowIndex += 1) {
            const rowIdx = plan.rowIndices[groupRowIndex];
            const excelRow = worksheet.getRow(rowIdx + 1);
            await replacePlaceholdersInExcelRow({
              excelRow,
              excelHeaderMap,
              item: plan.item,
              platform,
              stampName: plan.item?.stampName || '',
              dynamicValueConfigs,
              imageDownloadJobs,
              templateName,
              rowIndex: groupRowIndex,
              templateRow: plan.templateRow || null,
              templateHeaders: plan.templateHeaders || null,
              headerMapping: plan.headerMapping || null,
            });
          }
        }
      } else if (exportData?.length > 0) {
        const bootstrapRows = discoverExportableRowIndices(worksheet, dataStartRowIndex);
        const templateRowIndex = bootstrapRows[0] ?? dataStartRowIndex;
        const usedRowIndices = [];
        for (let itemIndex = 0; itemIndex < exportData.length; itemIndex += 1) {
          const item = exportData[itemIndex];
          let targetRowIndex;
          if (itemIndex < bootstrapRows.length && !usedRowIndices.includes(bootstrapRows[itemIndex])) {
            targetRowIndex = bootstrapRows[itemIndex];
          } else {
            const lastUsed =
              usedRowIndices.length > 0 ? Math.max(...usedRowIndices) : templateRowIndex;
            targetRowIndex = lastUsed + 1;
            copyWorksheetRowValues(worksheet, templateRowIndex, targetRowIndex);
          }
          usedRowIndices.push(targetRowIndex);
          const excelRow = worksheet.getRow(targetRowIndex + 1);
          await replacePlaceholdersInExcelRow({
            excelRow,
            excelHeaderMap,
            item,
            platform,
            stampName: item.stampName || '',
            dynamicValueConfigs,
            imageDownloadJobs,
            templateName,
          });
        }
      }

      // TEMU平台：导出完成前，统一处理SPU货号列
      // 规则：
      // - 同一批数据中，商品层级为 "spu" 的行，其 SPU货号 作为当前组的 SPU
      // - 之后商品层级为 "sku" 的行，若 SPU货号 为空，则强制填充为 当前组 SPU（不拼接"印花图名称"）
      // - 直到遇到下一行商品层级为 "spu" 时，更新当前组 SPU
      if (platform === 'TEMU半托' || platform === 'TEMU') {
        const temuWorksheet = worksheet;
        // 同时检查table2的excelHeaderMap和table1的excelHeaderMapTable1
        if (temuWorksheet) {
          // 找到 "商品层级" 和 "SPU货号" 对应的列
          let productLevelCol = null;
          let spuCodeCol = null;

          // 先检查table2的excelHeaderMap
          if (excelHeaderMap) {
            for (const [headerName, colIndex] of Object.entries(excelHeaderMap)) {
              if (
                headerName === '商品层级' ||
                headerName.includes('商品层级') ||
                headerName === '商品层级：商品层级' ||
                headerName.includes('商品层级：')
              ) {
                productLevelCol = colIndex;
              }
              if (headerName === 'SPU货号' || headerName.includes('SPU货号')) {
                spuCodeCol = colIndex;
              }
            }
          }
          
          // 如果table2没找到，再检查table1的excelHeaderMapTable1
          if ((!productLevelCol || !spuCodeCol) && excelHeaderMapTable1) {
            for (const [headerName, colIndex] of Object.entries(excelHeaderMapTable1)) {
              if (
                (!productLevelCol) && (
                  headerName === '商品层级' ||
                  headerName.includes('商品层级') ||
                  headerName === '商品层级：商品层级' ||
                  headerName.includes('商品层级：')
                )
              ) {
                productLevelCol = colIndex;
              }
              if ((!spuCodeCol) && (headerName === 'SPU货号' || headerName.includes('SPU货号'))) {
                spuCodeCol = colIndex;
              }
            }
          }

          if (productLevelCol && spuCodeCol) {
            const dataStartRow = getDataStartRowIndexByPlatform(platform);
            const lastRow = temuWorksheet.rowCount;
            let currentSpuBase = '';

            for (let rowIndex = dataStartRow; rowIndex <= lastRow; rowIndex++) {
              const row = temuWorksheet.getRow(rowIndex + 1);

              const levelCell = row.getCell(productLevelCol);
              const levelValueRaw = levelCell.value != null ? String(levelCell.value).trim().toLowerCase() : '';

              const spuCell = row.getCell(spuCodeCol);
              const spuRaw = spuCell.value != null ? String(spuCell.value).trim() : '';

              // 商品层级为 "spu"：更新当前组 SPU
              if (levelValueRaw === 'spu') {
                if (spuRaw) {
                  // 去掉占位符 "印花图名称"
                  const base = spuRaw.replace(/印花图名称/g, '').trim();
                  if (base) {
                    currentSpuBase = base;
                  }
                }
              }

              // 商品层级为 "sku" 且当前单元格为空：使用当前组 SPU 填充（不拼接"印花图名称"）
              if (levelValueRaw === 'sku') {
                const isEmpty =
                  spuCell.value === null ||
                  spuCell.value === undefined ||
                  (typeof spuCell.value === 'string' && spuCell.value.trim() === '');

                if (isEmpty && currentSpuBase) {
                  spuCell.value = currentSpuBase;
                }
              }
            }
          }
        }
      }

      // 15. 生成文件名并下载：模板名称_年月日时分秒.xlsx
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const rawTemplateName = String(template.name || template.title || templateName || templateId || '上架表格')
        .trim()
        .replace(/[\\/:*?"<>|]+/g, '_')
        .replace(/\s+/g, ' ')
        || '上架表格';
      const fileName = `${rawTemplateName}_${year}${month}${day}${hours}${minutes}${seconds}.xlsx`;
      // 16. 生成Excel文件并下载
      const buffer = await workbook.xlsx.writeBuffer();
      if (deferredExcelFiles) {
        deferredExcelFiles.push({ fileName, buffer, templateName: rawTemplateName });
      } else {
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      if (onProgress) {
        onProgress({
          current: templateIndex + 1,
          total: templateIds.length,
          templateId,
          status: 'completed',
          imageDownloadCount: imageDownloadJobs.list.length,
        });
      }
    }

    const shouldDownloadImages = options.runImageDownloads !== false;
    if (shouldDownloadImages && imageDownloadJobs.list.length > 0) {
      if (typeof options.onImageDownloadsStart === 'function') {
        options.onImageDownloadsStart(imageDownloadJobs.list.length);
      }
      await runExportImageDownloadBatch(imageDownloadJobs.list, options.downloadBatchOptions);
    }

    return {
      excelFiles: deferredExcelFiles || [],
      imageDownloadCount: imageDownloadJobs.list.length,
    };
  } catch (error) {
    console.error('[导出] 导出失败:', error);
    console.error('[导出] 错误堆栈:', error.stack);
    throw error;
  }
};
  
